// Vendor Imports
import _ from 'lodash';
import moment from 'moment';
import type { DateInput } from '@fullcalendar/core/datelib/env';

// Project Imports
import { dataProviders } from 'common/visualizations';
import { generateCustomColorPalette } from 'common/visualizations/dataManagers/CustomColorPaletteManager';
import getDefaultDomain from 'common/visualizations/helpers/getDefaultDomain';
import { type View } from 'common/types/view';
import { type ViewColumn } from 'common/types/viewColumn';
import { type SoqlFilter } from 'common/components/FilterBar/SoqlFilter';
import { type RowStripeStyle } from 'common/types/agGrid/rowStripe';
import { type MAP_TYPES } from 'common/visualizations/views/mapConstants';
import {
  type FontStyle,
  type FormatStyle,
  type HeaderFormatStyle,
  type TableColumnFormat,
} from 'common/visualizations/views/agGridReact/types';
import { type TableActionsContextField } from 'common/visualizations/views/agGridReact/TableActionsButton/types';
import { type ColumnAndAggregation } from 'common/authoring_workflow/components/shared/ColumnAndAggregationSelector';
import {
  type ColorPalette,
  type CustomLabels,
  type Drilldown,
  type Hierarchy,
  type MapCenterAndZoom,
  type MapPitchAndBearing,
  type OrderConfig,
  type Vif,
  type VisualizationAggregationFunction,
} from 'common/visualizations/vif';
import { VIF_CONSTANTS, type BASE_COLOR_PALETTE_NAMES, type QUANTIFICATION_METHOD_VALUES } from './constants';
import { load } from './vifs/loader';
import { type AuthoringWorkflowState, type VifAuthoring } from './reducers/types';
import {
  getColorPalette,
  getColorPaletteGroupingColumnName,
  getCustomColorPaletteForNewGLMaps,
  getCurrentSeriesIndex,
  getDimension,
  getEmptyVif,
  getMapLayerName,
  getPrimaryMapLayerSeriesIndex,
  getSeries,
  getVifs,
  getColorByQuantificationMethod,
  isMap,
  isNewGLMap,
  isPieChart,
} from './selectors/vifAuthoring';

type Thunk = (dispatch: Dispatch, getState: GetState) => any;
type Dispatch = (action: Action | Thunk) => any;
type GetState = () => AuthoringWorkflowState;

export const SET_VIF_CHECKPOINT = 'SET_VIF_CHECKPOINT';
export function setVifCheckpoint(vifs: Record<string, Vif>) {
  return {
    type: SET_VIF_CHECKPOINT,
    vifs,
  } as const;
}

export function setDataSource(
  domain: string | null | undefined,
  datasetUid: string | null | undefined,
  filters: SoqlFilter[] | null = null,
  useCache = true,
  seriesIndex = 0,
) {
  return (dispatch: Dispatch, getState: GetState) => {
    if (!datasetUid || !/\w{4}\-\w{4}/.test(datasetUid)) {
      return;
    }

    const resolvedDomain = domain || getDefaultDomain();
    const datasetMetadataProvider = new dataProviders.MetadataProvider(
      { domain: resolvedDomain, datasetUid },
      useCache,
    );
    const isUpdating = !useCache;

    const finishMetadataRequests = (
      datasetMetadata: View,
      baseViewMetadata: View,
      hasColumnStats: boolean,
      options: { domain: string; datasetUid: string; seriesIndex: number },
    ) => {
      const { vifAuthoring } = getState();
      dispatch(receiveMetadata(datasetMetadata, baseViewMetadata, hasColumnStats, options));
      dispatch(setVifCheckpoint(getVifs(vifAuthoring)));

      // We introduce name key for visualization_interchange_format 3.
      // which is not present in visualization_interchange_format 2.
      // existing saved VIF are in visualization_interchange_format 2.
      // when we edit the old VIF, it will become to format 3.
      // so the name key should not be there, since the VIF format was 2,
      // for that we set the name,source key and value.
      if (getMapLayerName(vifAuthoring) === VIF_CONSTANTS.DEFAULT_MAP_LAYER_NAME) {
        dispatch(setMapLayerName(_.get(datasetMetadata, 'name')));
      }

      const primaryMapLayerIndex = getPrimaryMapLayerSeriesIndex(vifAuthoring);

      if (isNewGLMap(vifAuthoring) && seriesIndex !== primaryMapLayerIndex) {
        // For NewGLMaps to support multi layers we need to auto populate the dimension with
        // geo location column on mapLayersPane load

        const dimension = getDimension(vifAuthoring);

        if (_.isNull(dimension.columnName)) {
          dispatch(setTriggerAutoSelectGeoLocationColumn(true));
        }
      }
    };

    dispatch(requestMetadata(resolvedDomain, datasetUid, isUpdating, seriesIndex));
    // On switching to a different dataset, update the table but not other chart types.
    // Other charts currently work with only the dataset from which the visualization was
    // created(primary dataset).
    dispatch(setDatasetUid(datasetUid, 'table'));
    dispatch(setDomain(resolvedDomain, 'table'));
    if (filters !== null) {
      dispatch(setFilters(filters, 'table'));
    }

    return Promise.all([
      // See comment in implementation to learn why we catch-all.
      datasetMetadataProvider.getBaseViewMetadata().catch(() => null),
      datasetMetadataProvider.getDatasetMetadata(),
    ])
      .then((resolutions) => {
        const datasetMetadata = resolutions[1];
        const baseViewMetadata = resolutions[0] || datasetMetadata;

        finishMetadataRequests(datasetMetadata, baseViewMetadata, true, {
          domain: resolvedDomain,
          datasetUid,
          seriesIndex,
        });
      })
      .catch((error) => {
        console.error(error);
        dispatch(handleMetadataError(error, seriesIndex));
      });
  };
}

export const REQUEST_METADATA = 'REQUEST_METADATA';
export function requestMetadata(domain: string, datasetUid: string, updating = false, seriesIndex = 0) {
  return {
    type: REQUEST_METADATA,
    domain,
    datasetUid,
    updating,
    seriesIndex,
  } as const;
}

export const RECEIVE_METADATA = 'RECEIVE_METADATA';
export function receiveMetadata(
  datasetMetadata: View,
  baseViewMetadata: View,
  hasColumnStats: boolean | null,
  requestDetails: { domain: string; datasetUid: string; seriesIndex: number },
) {
  return {
    type: RECEIVE_METADATA,
    datasetMetadata,
    baseViewMetadata,
    hasColumnStats,
    requestDetails,
  } as const;
}

export const HANDLE_METADATA_ERROR = 'HANDLE_METADATA_ERROR';
export function handleMetadataError(error: unknown, seriesIndex: number) {
  return {
    type: HANDLE_METADATA_ERROR,
    error,
    seriesIndex,
  } as const;
}

export function setCuratedRegions(domain: string, datasetUid: string, seriesIndex = 0) {
  return (dispatch: Dispatch) => {
    if (!/\w{4}\-\w{4}/.test(datasetUid)) {
      return;
    }

    const datasetMetadataProvider = new dataProviders.MetadataProvider({ domain, datasetUid }, true);

    dispatch(requestCuratedRegions(seriesIndex));

    return datasetMetadataProvider
      .getCuratedRegions()
      .then((curatedRegions: any) => {
        dispatch(receiveCuratedRegions(curatedRegions, seriesIndex));
      })
      .catch((error: unknown) => {
        console.error(error);
        dispatch(handleCuratedRegionsError(seriesIndex));
      });
  };
}

export function setComputedRegionColumns(domain: string, datasetUid: string, seriesIndex = 0) {
  return (dispatch: Dispatch) => {
    if (!/\w{4}\-\w{4}/.test(datasetUid)) {
      return;
    }

    const datasetMetadataProvider = new dataProviders.MetadataProvider({ domain, datasetUid }, true);

    dispatch(requestComputedRegionColumns(seriesIndex));

    return datasetMetadataProvider
      .getComputedRegionColumns()
      .then((computedRegionColumns: any) => {
        dispatch(receiveComputedRegionColumns(computedRegionColumns, seriesIndex));
      })
      .catch((error: unknown) => {
        console.error(error);
        dispatch(handleComputedRegionColumnsError(seriesIndex));
      });
  };
}

export const REQUEST_CURATED_REGIONS = 'REQUEST_CURATED_REGIONS';
export function requestCuratedRegions(seriesIndex: number) {
  return {
    type: REQUEST_CURATED_REGIONS,
    seriesIndex,
  } as const;
}

export const RECEIVE_CURATED_REGIONS = 'RECEIVE_CURATED_REGIONS';
export function receiveCuratedRegions(curatedRegions: any, seriesIndex: number) {
  return {
    type: RECEIVE_CURATED_REGIONS,
    curatedRegions,
    seriesIndex,
  } as const;
}

export const HANDLE_CURATED_REGIONS_ERROR = 'HANDLE_CURATED_REGIONS_ERROR';
export function handleCuratedRegionsError(seriesIndex: number) {
  return {
    type: HANDLE_CURATED_REGIONS_ERROR,
    seriesIndex,
  } as const;
}

export const REQUEST_COMPUTED_REGION_COLUMNS = 'REQUEST_COMPUTED_REGION_COLUMNS';
export function requestComputedRegionColumns(seriesIndex: number) {
  return {
    type: REQUEST_COMPUTED_REGION_COLUMNS,
    seriesIndex,
  } as const;
}

export const RECEIVE_COMPUTED_REGION_COLUMNS = 'RECEIVE_COMPUTED_REGION_COLUMNS';
export function receiveComputedRegionColumns(computedRegionColumns: any, seriesIndex: number) {
  return {
    type: RECEIVE_COMPUTED_REGION_COLUMNS,
    computedRegionColumns,
    seriesIndex,
  } as const;
}

export const HANDLE_COMPUTED_REGION_COLUMNS_ERROR = 'HANDLE_COMPUTED_REGION_COLUMNS_ERROR';
export function handleComputedRegionColumnsError(seriesIndex: number) {
  return {
    type: HANDLE_COMPUTED_REGION_COLUMNS_ERROR,
    seriesIndex,
  } as const;
}

export const SET_DATASET_UID = 'SET_DATASET_UID';
export function setDatasetUid(
  datasetUid: string | null | undefined,
  targetVisualizationType?: string,
  forceUpdate = false,
) {
  return {
    type: SET_DATASET_UID,
    datasetUid,
    targetVisualizationType,
    forceUpdate,
  } as const;
}

export const SET_DOMAIN = 'SET_DOMAIN';
export function setDomain(domain: string | null, targetVisualizationType?: string) {
  return {
    type: SET_DOMAIN,
    domain,
    targetVisualizationType,
  } as const;
}

export const SET_MAP_LAYER_NAME = 'SET_MAP_LAYER_NAME';
export function setMapLayerName(name: string) {
  return {
    type: SET_MAP_LAYER_NAME,
    name,
  } as const;
}

export const SET_MAP_LAYER_SOURCE = 'SET_MAP_LAYER_SOURCE';
export function setMapLayerSource(source: string) {
  return {
    type: SET_MAP_LAYER_SOURCE,
    source,
  } as const;
}

export const SET_MAP_LAYER_VISIBLE = 'SET_MAP_LAYER_VISIBLE';
export function setMapLayerVisible(relativeIndex: number, isVisible: boolean) {
  return {
    type: SET_MAP_LAYER_VISIBLE,
    relativeIndex,
    isVisible,
  } as const;
}

export const SET_SIMPLIFICATION_LEVEL = 'SET_SIMPLIFICATION_LEVEL';
export function setSimplificationLevel(relativeIndex: number, simplificationLevel: string) {
  return {
    type: SET_SIMPLIFICATION_LEVEL,
    relativeIndex,
    simplificationLevel,
  } as const;
}

export const SET_CHARM_NAME = 'SET_CHARM_NAME';
export function setCharmName(relativeIndex: number, charmName: string) {
  return {
    type: SET_CHARM_NAME,
    relativeIndex,
    charmName,
  } as const;
}

export const SET_FILTERS = 'SET_FILTERS';
export function setFilters(filters: SoqlFilter[] | undefined, targetVisualizationType?: string) {
  return {
    type: SET_FILTERS,
    filters,
    targetVisualizationType,
  } as const;
}

export const SET_TABLE_COLUMNS = 'SET_TABLE_COLUMNS';
export function setTableColumns(tableColumns: ViewColumn[]) {
  return {
    type: SET_TABLE_COLUMNS,
    tableColumns,
  } as const;
}

export const SET_TABLE_HIERARCHIES = 'SET_TABLE_HIERARCHIES';
export function setTableHierarchies(tableHierarchies: Hierarchy[]) {
  return {
    type: SET_TABLE_HIERARCHIES,
    tableHierarchies,
  } as const;
}

export const SET_TABLE_VIZ_COLUMN_FORMATS = 'SET_TABLE_VIZ_COLUMN_FORMATS';
export function setTableVisualizationColumnFormats(columnFormats: Record<string, TableColumnFormat>) {
  return {
    type: SET_TABLE_VIZ_COLUMN_FORMATS,
    columnFormats,
  } as const;
}

export const SET_DIMENSION = 'SET_DIMENSION';
export function setDimension(dimension: string | null) {
  return {
    type: SET_DIMENSION,
    dimension,
  } as const;
}

export const SET_CURRENT_DRILL_DOWN_COLUMN_NAME = 'SET_CURRENT_DRILL_DOWN_COLUMN_NAME';
export function setCurrentDrilldownColumnName(currentDrilldownColumnName: string) {
  return {
    type: SET_CURRENT_DRILL_DOWN_COLUMN_NAME,
    currentDrilldownColumnName,
  } as const;
}

export const SET_DRILL_DOWNS = 'SET_DRILL_DOWNS';
export function setDrilldowns(drilldowns: Drilldown[]) {
  return {
    type: SET_DRILL_DOWNS,
    drilldowns,
  } as const;
}

export const APPEND_SERIES = 'APPEND_SERIES';
export function appendSeries({
  isFlyoutSeries,
  isInitialLoad,
  isMapSeries = false,
  measureAggregationFunction,
  measureColumnName,
  rowDisplayUnit,
  seriesVariant,
}: {
  isFlyoutSeries?: boolean;
  isInitialLoad?: boolean;
  isMapSeries?: boolean;
  measureAggregationFunction?: VisualizationAggregationFunction | string | null;
  measureColumnName?: string | null;
  rowDisplayUnit?: string | null;
  seriesVariant?: string | null;
}) {
  return {
    type: APPEND_SERIES,
    isFlyoutSeries,
    isInitialLoad,
    isMapSeries,
    measureAggregationFunction,
    measureColumnName,
    rowDisplayUnit,
    seriesVariant,
  } as const;
}

export const REMOVE_SERIES = 'REMOVE_SERIES';
export function removeSeries({
  isFlyoutSeries,
  relativeIndex,
  isMapSeries = false,
}: {
  isFlyoutSeries?: boolean;
  relativeIndex: number;
  isMapSeries?: boolean;
}) {
  return {
    type: REMOVE_SERIES,
    isFlyoutSeries,
    relativeIndex,
    isMapSeries,
  } as const;
}

export const REMOVE_METADATA_SERIES = 'REMOVE_METADATA_SERIES';
export function removeMetadataSeries(seriesIndex: number) {
  return {
    type: REMOVE_METADATA_SERIES,
    seriesIndex,
  } as const;
}

export const SWAP_SERIES = 'SWAP_SERIES';
export function swapSeries(fromIndex: number, toIndex: number) {
  return {
    type: SWAP_SERIES,
    fromIndex,
    toIndex,
  } as const;
}

export const SWAP_COLOR_PALETTE = 'SWAP_COLOR_PALETTE';
export function swapColorPalette(columnName: string, fromIndex: number, toIndex: number) {
  return {
    type: SWAP_COLOR_PALETTE,
    columnName,
    fromIndex,
    toIndex,
  } as const;
}

export const SWAP_COLOR_PALETTE_WITH_QUANTIFICATION = 'SWAP_COLOR_PALETTE_WITH_QUANTIFICATION';
export function swapColorPaletteWithQuantification(
  columnName: string,
  fromIndex: number,
  toIndex: number,
  quantificationMethod: QUANTIFICATION_METHOD_VALUES,
) {
  return {
    type: SWAP_COLOR_PALETTE_WITH_QUANTIFICATION,
    columnName,
    fromIndex,
    toIndex,
    quantificationMethod,
  } as const;
}

export const SWAP_METADATA_COLLECTION_SERIES = 'SWAP_METADATA_COLLECTION_SERIES';
export function swapMetaDataCollectionSeries(fromIndex: number, toIndex: number) {
  return {
    type: SWAP_METADATA_COLLECTION_SERIES,
    fromIndex,
    toIndex,
  } as const;
}

export const SET_SERIES_VARIANT = 'SET_SERIES_VARIANT';
export function setSeriesVariant(seriesIndex: number, seriesVariant: string) {
  return {
    type: SET_SERIES_VARIANT,
    seriesIndex,
    seriesVariant,
  } as const;
}

export const SET_MEASURE_AGGREGATION = 'SET_MEASURE_AGGREGATION';
export function setMeasureAggregation({
  aggregationFunction,
  isFlyoutSeries,
  relativeIndex,
}: {
  aggregationFunction: VisualizationAggregationFunction | string | null;
  isFlyoutSeries?: boolean;
  relativeIndex?: number;
}) {
  return {
    type: SET_MEASURE_AGGREGATION,
    aggregationFunction,
    isFlyoutSeries,
    relativeIndex,
  } as const;
}

export const SET_MEASURE_COLUMN = 'SET_MEASURE_COLUMN';
export function setMeasureColumn({
  columnName,
  isFlyoutSeries,
  relativeIndex,
}: {
  columnName: string | null;
  isFlyoutSeries?: boolean;
  relativeIndex?: number;
}) {
  return {
    type: SET_MEASURE_COLUMN,
    columnName,
    isFlyoutSeries,
    relativeIndex,
  } as const;
}

export const APPEND_REFERENCE_LINE = 'APPEND_REFERENCE_LINE';
export function appendReferenceLine() {
  return {
    type: APPEND_REFERENCE_LINE,
  } as const;
}

export const REMOVE_REFERENCE_LINE = 'REMOVE_REFERENCE_LINE';
export function removeReferenceLine(referenceLineIndex: number) {
  return {
    type: REMOVE_REFERENCE_LINE,
    referenceLineIndex,
  } as const;
}

export const SET_REFERENCE_LINE_COLOR = 'SET_REFERENCE_LINE_COLOR';
export function setReferenceLineColor({
  referenceLineIndex,
  color,
}: {
  referenceLineIndex: number;
  color: string;
}) {
  return {
    type: SET_REFERENCE_LINE_COLOR,
    referenceLineIndex,
    color,
  } as const;
}

export const SET_REFERENCE_LINE_LABEL = 'SET_REFERENCE_LINE_LABEL';
export function setReferenceLineLabel({
  referenceLineIndex,
  label,
}: {
  referenceLineIndex: number;
  label: string;
}) {
  return {
    type: SET_REFERENCE_LINE_LABEL,
    referenceLineIndex,
    label,
  } as const;
}

export const SET_REFERENCE_LINE_VALUE = 'SET_REFERENCE_LINE_VALUE';
export function setReferenceLineValue({
  referenceLineIndex,
  value,
}: {
  referenceLineIndex: number;
  value: number | null;
}) {
  return {
    type: SET_REFERENCE_LINE_VALUE,
    referenceLineIndex,
    value,
  } as const;
}

export const SET_VISUALIZATION_TYPE = 'SET_VISUALIZATION_TYPE';
export function setVisualizationType(visualizationType: string) {
  return {
    type: SET_VISUALIZATION_TYPE,
    visualizationType,
  } as const;
}

export const INITIATE_REGION_CODING = 'INITIATE_REGION_CODING';
export function initiateRegionCoding(
  domain: string,
  datasetUid: string | null,
  sourceColumn: string | undefined,
  curatedRegion: { uid: string; featurePk?: string; geometryLabel?: string },
) {
  return (dispatch: Dispatch, getState: GetState) => {
    if (!datasetUid) {
      return;
    }

    const providerOptions = { domain, datasetUid };
    const datasetMetadataProvider = new dataProviders.MetadataProvider(providerOptions);
    // RegionCodingProvider is still plain JS (function-constructor style), so TS infers wrong
    // method signatures for it (e.g. `Function`/`Object` return types, extra required params).
    // Cast to `any` rather than fixing that inference here — converting it is a separate ticket.
    const regionCodingProvider: any = new dataProviders.RegionCodingProvider(providerOptions);
    const { featurePk, geometryLabel, uid } = curatedRegion;
    const handleError = (error?: unknown) => {
      if (checkAndAbortOnShapefileIdMismatch()) {
        return;
      }

      dispatch(handleRegionCodingError(error));
      dispatch(setRegionCodingShapefileId(null));
    };
    const handleCompletion = () => {
      if (checkAndAbortOnShapefileIdMismatch()) {
        return;
      }

      datasetMetadataProvider
        .getDatasetMetadata()
        .then((response: View) => {
          const { columns } = response;
          const computedColumn = _.find(columns, (column) => {
            return _.get(column, 'computationStrategy.parameters.region', '').slice(1) === uid;
          });

          dispatch(finishRegionCoding());
          dispatch(setComputedColumn(computedColumn?.fieldName ?? null));
          dispatch(setShapefile(uid, featurePk, geometryLabel));
          dispatch(setCenterAndZoom(null));

          // fetch the latest data source so that the region selector will
          // be updated with latest computed columns and curated regions
          dispatch(setDataSource(domain, datasetUid, null, false));
          dispatch(setRegionCodingShapefileId(null));
        })
        .catch(handleError);
    };
    const handleInitiation = (response: { success: boolean; jobId: string }) => {
      if (response.success) {
        regionCodingProvider
          .awaitRegionCodingCompletion({ jobId: response.jobId }, handleStatus)
          .then(handleCompletion)
          .catch(handleError);
      } else {
        handleError();
      }
    };
    const checkAndAbortOnShapefileIdMismatch = () => {
      const { regionCodingShapefileIdInProcess } = getState().vifAuthoring.authoring;

      if (regionCodingShapefileIdInProcess !== uid) {
        regionCodingProvider.abortPollingForRegionCodingStatus();
        return true;
      }

      return false;
    };
    const handleStatus = (response: { status: string; data?: { jobId: string } }) => {
      if (checkAndAbortOnShapefileIdMismatch()) {
        return;
      }

      switch (response.status) {
        case 'unknown':
          regionCodingProvider
            .initiateRegionCoding(uid, sourceColumn)
            .then(handleInitiation)
            .catch(handleError);
          break;
        case 'processing':
          dispatch(awaitRegionCoding());
          regionCodingProvider
            .awaitRegionCodingCompletion({ jobId: response.data?.jobId })
            .then(handleCompletion)
            .catch(handleError);
          break;
        case 'completed':
          handleCompletion();
          break;
        case 'await':
          dispatch(awaitRegionCoding());
          break;
        default:
          handleError();
          break;
      }
    };

    dispatch(setShapefileUid(uid));
    dispatch(setComputedColumn(null));
    dispatch(setRegionCodingShapefileId(uid));
    dispatch(requestRegionCoding());

    return regionCodingProvider
      .getRegionCodingStatus({ shapefileId: uid })
      .then(handleStatus)
      .catch(handleError);
  };
}

export const REQUEST_REGION_CODING = 'REQUEST_REGION_CODING';
export function requestRegionCoding() {
  return {
    type: REQUEST_REGION_CODING,
  } as const;
}

export const SET_REGION_CODING_SHAPEFILE_ID = 'SET_REGION_CODING_SHAPEFILE_ID';
export function setRegionCodingShapefileId(shapefileId: string | null) {
  return {
    type: SET_REGION_CODING_SHAPEFILE_ID,
    shapefileId,
  } as const;
}

export const AWAIT_REGION_CODING = 'AWAIT_REGION_CODING';
export function awaitRegionCoding() {
  return {
    type: AWAIT_REGION_CODING,
    updatedAt: moment().calendar(),
  } as const;
}

export const FINISH_REGION_CODING = 'FINISH_REGION_CODING';
export function finishRegionCoding() {
  return {
    type: FINISH_REGION_CODING,
  } as const;
}

export const HIDE_REGION_CODING_ERROR = 'HIDE_REGION_CODING_ERROR';
export function hideRegionCodingError() {
  return {
    type: HIDE_REGION_CODING_ERROR,
  } as const;
}

export const HANDLE_REGION_CODING_ERROR = 'HANDLE_REGION_CODING_ERROR';
export function handleRegionCodingError(error: unknown) {
  return {
    type: HANDLE_REGION_CODING_ERROR,
    error,
  } as const;
}

export const SET_COMPUTED_COLUMN = 'SET_COMPUTED_COLUMN';
export function setComputedColumn(computedColumn: string | null) {
  return {
    type: SET_COMPUTED_COLUMN,
    computedColumn,
  } as const;
}

export const SET_COMPUTED_COLUMN_FOR_SHAPE_FILE = 'SET_COMPUTED_COLUMN_FOR_SHAPE_FILE';
export function setComputedColumnForShapeFile(computedColumn: string, shapeFileUid: string) {
  return {
    type: SET_COMPUTED_COLUMN_FOR_SHAPE_FILE,
    computedColumn,
    shapeFileUid,
  } as const;
}

export const REQUEST_SHAPEFILE_METADATA = 'REQUEST_SHAPEFILE_METADATA';
export function requestShapefileMetadata(domain: string, shapefileUid: string) {
  return (dispatch: Dispatch, getState: GetState) => {
    const shapefileMetadataProvider = new dataProviders.MetadataProvider({
      domain,
      datasetUid: shapefileUid,
    });
    const handleShapefileMetadata = ({
      featurePk,
      geometryLabel,
    }: {
      featurePk: string | null;
      geometryLabel: string | null;
    }) => {
      dispatch(setCenterAndZoom(null));
      dispatch(setShapefile(shapefileUid, featurePk, geometryLabel));
    };

    dispatch(requestShapefile());

    const { showRegionCodingProcessingMessage, regionCodingError } = getState().vifAuthoring.authoring;

    if (showRegionCodingProcessingMessage === true) {
      dispatch(finishRegionCoding());
      dispatch(setRegionCodingShapefileId(null));
    }

    if (regionCodingError === true) {
      dispatch(hideRegionCodingError());
    }

    // getShapefileMetadata() takes no arguments; it reads datasetUid from this provider's own
    // config, which is already set to shapefileUid above.
    return shapefileMetadataProvider.getShapefileMetadata().then(handleShapefileMetadata);
  };
}

export const REQUEST_SHAPEFILE = 'REQUEST_SHAPEFILE';
export function requestShapefile() {
  return {
    type: REQUEST_SHAPEFILE,
  } as const;
}

export const SET_SHAPEFILE = 'SET_SHAPEFILE';
export function setShapefile(
  shapefileUid: string,
  shapefilePrimaryKey: string | null | undefined,
  shapefileGeometryLabel: string | null | undefined,
) {
  return {
    type: SET_SHAPEFILE,
    shapefileUid,
    shapefilePrimaryKey,
    shapefileGeometryLabel,
  } as const;
}

export const SET_SHAPEFILE_UID = 'SET_SHAPEFILE_UID';
export function setShapefileUid(shapefileUid: string) {
  return {
    type: SET_SHAPEFILE_UID,
    shapefileUid,
  } as const;
}

export const SET_SHAPEFILE_PRIMARY_KEY = 'SET_SHAPEFILE_PRIMARY_KEY';
export function setShapefilePrimaryKey(shapefilePrimaryKey: string) {
  return {
    type: SET_SHAPEFILE_PRIMARY_KEY,
    shapefilePrimaryKey,
  } as const;
}

export const SET_SHAPEFILE_GEOMETRY_LABEL = 'SET_SHAPEFILE_GEOMETRY_LABEL';
export function setShapefileGeometryLabel(shapefileGeometryLabel: string) {
  return {
    type: SET_SHAPEFILE_GEOMETRY_LABEL,
    shapefileGeometryLabel,
  } as const;
}

export const SET_TITLE = 'SET_TITLE';
export function setTitle(title: string) {
  return {
    type: SET_TITLE,
    title,
  } as const;
}

export const SET_DESCRIPTION = 'SET_DESCRIPTION';
export function setDescription(description: string) {
  return {
    type: SET_DESCRIPTION,
    description,
  } as const;
}

export const SET_VIEW_SOURCE_DATA_LINK = 'SET_VIEW_SOURCE_DATA_LINK';
export function setViewSourceDataLink(viewSourceDataLink: boolean) {
  return {
    type: SET_VIEW_SOURCE_DATA_LINK,
    viewSourceDataLink,
  } as const;
}

export const SET_SHOW_TABLE_ACTIONS = 'SET_SHOW_TABLE_ACTIONS';
export function setShowTableActions(showAction: boolean) {
  return {
    type: SET_SHOW_TABLE_ACTIONS,
    showAction,
  } as const;
}

export const SET_SHOW_EXISTING_ACTION_COLUMN = 'SET_SHOW_EXISTING_ACTION_COLUMN';
export function setShowExistingActionColumn(showExistingActionColumn: boolean) {
  return {
    type: SET_SHOW_EXISTING_ACTION_COLUMN,
    showExistingActionColumn,
  } as const;
}

export const SET_SELECTED_EXISTING_COLUMN = 'SET_SELECTED_EXISTING_COLUMN';
export function setSelectedExistingColumn(selectedExistingColumn: string) {
  return {
    type: SET_SELECTED_EXISTING_COLUMN,
    selectedExistingColumn,
  } as const;
}

export const SET_SELECTED_ACTION_FOR_EXISTING_COLUMN = 'SET_SELECTED_ACTION_FOR_EXISTING_COLUMN';
export function setSelectedActionForExistingColumn(selectedActionForExistingColumn: string) {
  return {
    type: SET_SELECTED_ACTION_FOR_EXISTING_COLUMN,
    selectedActionForExistingColumn,
  } as const;
}

// Here, newTableActions will be the selected options that are rendered within the AG Grid table cell inside the Actions menu drop-down.
export const SET_NEW_TABLE_ACTIONS = 'SET_NEW_TABLE_ACTIONS';
export function setNewTableActions(newTableActions: string[]) {
  return {
    type: SET_NEW_TABLE_ACTIONS,
    newTableActions,
  } as const;
}

export const SET_TABLE_ACTIONS_MEDIA_TYPE = 'SET_TABLE_ACTIONS_MEDIA_TYPE';
export function setTableActionsMediaType(mediaType: string) {
  return {
    type: SET_TABLE_ACTIONS_MEDIA_TYPE,
    mediaType,
  } as const;
}

export const SET_TABLE_ACTIONS_CONTEXT_FIELDS_MAPPING = 'SET_TABLE_ACTIONS_CONTEXT_FIELDS_MAPPING';
export function setTableActionsContextFieldsMapping(contextFieldMappings: TableActionsContextField[]) {
  return {
    type: SET_TABLE_ACTIONS_CONTEXT_FIELDS_MAPPING,
    contextFieldMappings,
  } as const;
}

export const SET_PRIMARY_COLOR = 'SET_PRIMARY_COLOR';
export function setPrimaryColor(seriesIndex: number, primaryColor: string) {
  return {
    type: SET_PRIMARY_COLOR,
    seriesIndex,
    primaryColor,
  } as const;
}

export const SET_SECONDARY_COLOR = 'SET_SECONDARY_COLOR';
export function setSecondaryColor(seriesIndex: number, secondaryColor: string) {
  return {
    type: SET_SECONDARY_COLOR,
    seriesIndex,
    secondaryColor,
  } as const;
}

export const SET_COLOR_SCALE = 'SET_COLOR_SCALE';
export function setColorScale(negativeColor: string, zeroColor: string, positiveColor: string) {
  return {
    type: SET_COLOR_SCALE,
    negativeColor,
    zeroColor,
    positiveColor,
  } as const;
}

export const SET_COLOR_PALETTE_PROPERTIES = 'SET_COLOR_PALETTE_PROPERTIES';
export function setColorPaletteProperties() {
  return (dispatch: Dispatch, getState: GetState) => {
    const { vifAuthoring } = getState();
    const currentSeriesIndex = getCurrentSeriesIndex(vifAuthoring);
    const quantificationMethod = getColorByQuantificationMethod(vifAuthoring);
    const hasCustomColorPalette = _.isEqual(getColorPalette()(vifAuthoring), 'custom');
    let customColorPaletteForNewGLMaps: any = null;
    let colorPaletteGroupingColumnName: string | null = null;
    dispatch({ type: SET_COLOR_PALETTE_PROPERTIES });

    if (hasCustomColorPalette) {
      // isNewGLMap returns a boolean, so this comparison to 'map' is always false. Pre-existing
      // dead branch preserved as-is (not fixed here per ts-conversion scope; see PR notes).
      if ((isNewGLMap(vifAuthoring) as unknown as string) === 'map') {
        colorPaletteGroupingColumnName = getColorPaletteGroupingColumnName(vifAuthoring) ?? null;
        if (!_.isNull(colorPaletteGroupingColumnName)) {
          customColorPaletteForNewGLMaps = getCustomColorPaletteForNewGLMaps(vifAuthoring);
        }
      }

      generateCustomColorPalette(vifAuthoring)
        .then(({ customColorPalette, dimensionColumnName }) => {
          // if (!_.isNull(colorPaletteGroupingColumnName)) {
          //   if (isMap(vifAuthoring) && quantificationMethod) {
          //     dispatch(setCustomColorPaletteWithQuantification(customColorPalette, colorPaletteGroupingColumnName, currentSeriesIndex, quantificationMethod));
          //   } else {
          //     dispatch(setCustomColorPalette(customColorPalette, colorPaletteGroupingColumnName, currentSeriesIndex));
          //   }
          // } else {
          //   if (isMap(vifAuthoring) && quantificationMethod) {
          //     dispatch(setCustomColorPaletteWithQuantification(customColorPalette, dimensionColumnName, currentSeriesIndex, quantificationMethod));
          //   } else {
          //     dispatch(setCustomColorPalette(customColorPalette, dimensionColumnName, currentSeriesIndex));
          //   }
          // }
          setCustomColorPaletteForAllVizType({
            colorPaletteGroupingColumnName,
            customColorPalette,
            currentSeriesIndex,
            dimensionColumnName,
            dispatch,
            vifAuthoring,
            quantificationMethod,
          });

          if (!_.isNull(customColorPaletteForNewGLMaps)) {
            _.each(customColorPaletteForNewGLMaps, (group: any, palette: any) => {
              if (isMap(vifAuthoring) && quantificationMethod) {
                updateCustomColorPaletteWithQuantification(
                  palette.color,
                  group,
                  colorPaletteGroupingColumnName,
                  currentSeriesIndex,
                  quantificationMethod,
                );
                updateCustomCharmNameWithQuantification(
                  palette.charmName,
                  group,
                  colorPaletteGroupingColumnName,
                  currentSeriesIndex,
                  quantificationMethod,
                );
              } else {
                updateCustomColorPalette(
                  palette.color,
                  group,
                  colorPaletteGroupingColumnName,
                  currentSeriesIndex,
                );
                updateCustomCharmName(
                  palette.charmName,
                  group,
                  colorPaletteGroupingColumnName,
                  currentSeriesIndex,
                );
              }
            });
          }
        })
        .catch((error) => {
          dispatch(handleCustomColorPaletteError(error));
          console.error(error);
        });
    }
  };
}

export const HANDLE_CUSTOM_COLOR_PALETTE_ERROR = 'HANDLE_CUSTOM_COLOR_PALETTE_ERROR';
export function handleCustomColorPaletteError(error: unknown) {
  return {
    type: HANDLE_CUSTOM_COLOR_PALETTE_ERROR,
    error,
  } as const;
}

export const SET_COLOR_PALETTE = 'SET_COLOR_PALETTE';
export function setColorPalette(colorPalette: BASE_COLOR_PALETTE_NAMES | 'custom' | string | null) {
  return {
    type: SET_COLOR_PALETTE,
    colorPalette,
  } as const;
}

export const SET_NEGATIVE_COLOR = 'SET_NEGATIVE_COLOR';
export function setNegativeColor(negativeColor: string) {
  return {
    type: SET_NEGATIVE_COLOR,
    negativeColor,
  } as const;
}

export const SET_ZERO_COLOR = 'SET_ZERO_COLOR';
export function setZeroColor(zeroColor: string) {
  return {
    type: SET_ZERO_COLOR,
    zeroColor,
  } as const;
}
export const SET_POSITIVE_COLOR = 'SET_POSITIVE_COLOR';
export function setPositiveColor(positiveColor: string) {
  return {
    type: SET_POSITIVE_COLOR,
    positiveColor,
  } as const;
}

export const SET_BASE_LAYER = 'SET_BASE_LAYER';
export function setBaseLayer(baseLayer: string) {
  return {
    type: SET_BASE_LAYER,
    baseLayer,
  } as const;
}

export const SET_BASE_LAYER_OPACITY = 'SET_BASE_LAYER_OPACITY';
export function setBaseLayerOpacity(baseLayerOpacity: number) {
  return {
    type: SET_BASE_LAYER_OPACITY,
    baseLayerOpacity,
  } as const;
}

export const SET_LABEL_TOP = 'SET_LABEL_TOP';
export function setLabelTop(labelTop: string) {
  return {
    type: SET_LABEL_TOP,
    labelTop,
  } as const;
}

export const SET_LABEL_LEFT = 'SET_LABEL_LEFT';
export function setLabelLeft(labelLeft: string) {
  return {
    type: SET_LABEL_LEFT,
    labelLeft,
  } as const;
}

export const SET_LABEL_BOTTOM = 'SET_LABEL_BOTTOM';
export function setLabelBottom(labelBottom: string) {
  return {
    type: SET_LABEL_BOTTOM,
    labelBottom,
  } as const;
}

export const SET_LABEL_RIGHT = 'SET_LABEL_RIGHT';
export function setLabelRight(labelRight: string) {
  return {
    type: SET_LABEL_RIGHT,
    labelRight,
  } as const;
}

export const SET_LEGEND_POSITION = 'SET_LEGEND_POSITION';
export function setLegendPosition(position: string | null) {
  return {
    type: SET_LEGEND_POSITION,
    position,
  } as const;
}

export const SET_SHOW_DIMENSION_LABELS = 'SET_SHOW_DIMENSION_LABELS';
export function setShowDimensionLabels(showDimensionLabels: boolean) {
  return {
    type: SET_SHOW_DIMENSION_LABELS,
    showDimensionLabels,
  } as const;
}

export const SET_SHOW_LINE_VALUE_LABELS = 'SET_SHOW_LINE_VALUE_LABELS';
export function setShowLineValueLabels(showLineValueLabels: boolean) {
  return {
    type: SET_SHOW_LINE_VALUE_LABELS,
    showLineValueLabels,
  } as const;
}

export const SET_SHOW_NULLS_AS_FALSE = 'SET_SHOW_NULLS_AS_FALSE';
export function setShowNullsAsFalse(showNullsAsFalse: boolean) {
  return {
    type: SET_SHOW_NULLS_AS_FALSE,
    showNullsAsFalse,
  } as const;
}

export const SET_SHOW_VALUE_LABELS = 'SET_SHOW_VALUE_LABELS';
export function setShowValueLabels(showValueLabels: boolean) {
  return {
    type: SET_SHOW_VALUE_LABELS,
    showValueLabels,
  } as const;
}

export const SET_SHOW_VALUE_LABELS_AS_PERCENT = 'SET_SHOW_VALUE_LABELS_AS_PERCENT';
export function setShowValueLabelsAsPercent(showValueLabelsAsPercent: boolean) {
  return {
    type: SET_SHOW_VALUE_LABELS_AS_PERCENT,
    showValueLabelsAsPercent,
  } as const;
}

export const SET_SHOW_LEGEND = 'SET_SHOW_LEGEND';
export function setShowLegend(showLegend: boolean) {
  return {
    type: SET_SHOW_LEGEND,
    showLegend,
  } as const;
}

export const SET_SHOW_MULTIPLE_POINTS_SYMBOL_IN_LEGEND = 'SET_SHOW_MULTIPLE_POINTS_SYMBOL_IN_LEGEND';
export function setShowMultiplePointsSymbolInLegend(showMultiplePointsSymbolInLegend: boolean) {
  return {
    type: SET_SHOW_MULTIPLE_POINTS_SYMBOL_IN_LEGEND,
    showMultiplePointsSymbolInLegend,
  } as const;
}

export const SET_SHOW_LEGEND_FOR_MAP = 'SET_SHOW_LEGEND_FOR_MAP';
export function setShowLegendForMap(showLegendForMap: boolean) {
  return {
    type: SET_SHOW_LEGEND_FOR_MAP,
    showLegendForMap,
  } as const;
}

export const SET_SHOW_LEGEND_OPENED = 'SET_SHOW_LEGEND_OPENED';
export function setShowLegendOpened(showLegendOpened: boolean) {
  return {
    type: SET_SHOW_LEGEND_OPENED,
    showLegendOpened,
  } as const;
}

export const SET_WRAP_DIMENSION_LABELS = 'SET_WRAP_DIMENSION_LABELS';
export function setWrapDimensionLabels(wrapDimensionLabels: boolean) {
  return {
    type: SET_WRAP_DIMENSION_LABELS,
    wrapDimensionLabels,
  } as const;
}

export const SET_X_AXIS_SCALING_MODE = 'SET_X_AXIS_SCALING_MODE';
export function setXAxisScalingMode({ shouldFit }: { shouldFit: boolean }) {
  return {
    type: SET_X_AXIS_SCALING_MODE,
    xAxisScalingMode: shouldFit ? 'fit' : 'pan',
  } as const;
}

export const SET_MEASURE_AXIS_MAX_VALUE = 'SET_MEASURE_AXIS_MAX_VALUE';
export function setMeasureAxisMaxValue(measureAxisMaxValue?: number | string) {
  return {
    type: SET_MEASURE_AXIS_MAX_VALUE,
    measureAxisMaxValue,
  } as const;
}

export const SET_MEASURE_AXIS_MIN_VALUE = 'SET_MEASURE_AXIS_MIN_VALUE';
export function setMeasureAxisMinValue(measureAxisMinValue?: number | string) {
  return {
    type: SET_MEASURE_AXIS_MIN_VALUE,
    measureAxisMinValue,
  } as const;
}

export const SET_MEASURE_AXIS_PRECISION = 'SET_MEASURE_AXIS_PRECISION';
export function setMeasureAxisPrecision(precision?: number) {
  return {
    type: SET_MEASURE_AXIS_PRECISION,
    precision,
  } as const;
}

export const SET_LOGARITHMIC_SCALE = 'SET_LOGARITHMIC_SCALE';
export function setLogarithmicScale(logarithmicScale: boolean) {
  return {
    type: SET_LOGARITHMIC_SCALE,
    logarithmicScale,
  } as const;
}

export const SET_MAP_LEGEND_PRECISION = 'SET_MAP_LEGEND_PRECISION';
export function setMapLegendPrecision(precision?: number) {
  return {
    type: SET_MAP_LEGEND_PRECISION,
    precision,
  } as const;
}

export const SET_MAP_FLYOUT_PRECISION = 'SET_MAP_FLYOUT_PRECISION';
export function setMapFlyoutPrecision(precision?: number) {
  return {
    type: SET_MAP_FLYOUT_PRECISION,
    precision,
  } as const;
}

export const SET_MEASURE_AXIS_SCALE = 'SET_MEASURE_AXIS_SCALE';
export function setMeasureAxisScale(scale?: 'min_to_max') {
  return {
    type: SET_MEASURE_AXIS_SCALE,
    scale,
  } as const;
}

export const SET_SECONDARY_MEASURE_AXIS_MAX_VALUE = 'SET_SECONDARY_MEASURE_AXIS_MAX_VALUE';
export function setSecondaryMeasureAxisMaxValue(measureAxisMaxValue?: number | string) {
  return {
    type: SET_SECONDARY_MEASURE_AXIS_MAX_VALUE,
    measureAxisMaxValue,
  } as const;
}

export const SET_SECONDARY_MEASURE_AXIS_MIN_VALUE = 'SET_SECONDARY_MEASURE_AXIS_MIN_VALUE';
export function setSecondaryMeasureAxisMinValue(measureAxisMinValue?: number | string) {
  return {
    type: SET_SECONDARY_MEASURE_AXIS_MIN_VALUE,
    measureAxisMinValue,
  } as const;
}

export const SET_SECONDARY_MEASURE_AXIS_PRECISION = 'SET_SECONDARY_MEASURE_AXIS_PRECISION';
export function setSecondaryMeasureAxisPrecision(precision?: number) {
  return {
    type: SET_SECONDARY_MEASURE_AXIS_PRECISION,
    precision,
  } as const;
}
export const SET_SECONDARY_MEASURE_AXIS_SCALE = 'SET_SECONDARY_MEASURE_AXIS_SCALE';
export function setSecondaryMeasureAxisScale(scale?: 'min_to_max') {
  return {
    type: SET_SECONDARY_MEASURE_AXIS_SCALE,
    scale,
  } as const;
}

export const SET_UNIT_ONE = 'SET_UNIT_ONE';
export function setUnitsOne(seriesIndex: number, one: string) {
  return {
    type: SET_UNIT_ONE,
    seriesIndex,
    one,
  } as const;
}

export const SET_UNIT_OTHER = 'SET_UNIT_OTHER';
export function setUnitsOther(seriesIndex: number, other: string) {
  return {
    type: SET_UNIT_OTHER,
    seriesIndex,
    other,
  } as const;
}

export const SET_ROW_INSPECTOR_TITLE_COLUMN_NAME = 'SET_ROW_INSPECTOR_TITLE_COLUMN_NAME';
export function setRowInspectorTitleColumnName(rowInspectorTitleColumnName: string) {
  return {
    type: SET_ROW_INSPECTOR_TITLE_COLUMN_NAME,
    rowInspectorTitleColumnName,
  } as const;
}

export const SET_SHOW_SLICE_PERCENTS_IN_FLYOUTS = 'SET_SHOW_SLICE_PERCENTS_IN_FLYOUTS';
export function setShowSlicePercentsInFlyouts(showSlicePercentsInFlyouts: boolean) {
  return {
    type: SET_SHOW_SLICE_PERCENTS_IN_FLYOUTS,
    showSlicePercentsInFlyouts,
  } as const;
}

export const SET_DIMENSION_LABEL_AREA_SIZE = 'SET_DIMENSION_LABEL_AREA_SIZE';
export function setDimensionLabelAreaSize(width: number) {
  return {
    type: SET_DIMENSION_LABEL_AREA_SIZE,
    width,
  } as const;
}

export const SET_DIMENSION_LABEL_ROTATION_ANGLE = 'SET_DIMENSION_LABEL_ROTATION_ANGLE';
export function setDimensionLabelRotationAngle(angle: number) {
  return {
    type: SET_DIMENSION_LABEL_ROTATION_ANGLE,
    angle,
  } as const;
}

export const SET_CENTER_AND_ZOOM = 'SET_CENTER_AND_ZOOM';
export function setCenterAndZoom(centerAndZoom: MapCenterAndZoom | null) {
  return {
    type: SET_CENTER_AND_ZOOM,
    centerAndZoom,
  } as const;
}

export const SET_MAP_INFO_DISMISSED = 'SET_MAP_INFO_DISMISSED';
export function setMapInfoDismissed() {
  return {
    type: SET_MAP_INFO_DISMISSED,
  } as const;
}

export const SET_CURRENT_MAP_LAYER_INDEX = 'SET_CURRENT_MAP_LAYER_INDEX';
export function setCurrentMapLayerIndex(currentMapLayerIndex = 0) {
  return {
    type: SET_CURRENT_MAP_LAYER_INDEX,
    currentMapLayerIndex,
  } as const;
}

export const SET_LAYER_AS_PRIMARY = 'SET_LAYER_AS_PRIMARY';
export function setLayerAsPrimary(relativeIndex: number, isPrimary: boolean) {
  return {
    type: SET_LAYER_AS_PRIMARY,
    relativeIndex,
    isPrimary,
  } as const;
}

export const SET_CURRENT_MAP_LAYER_MODE = 'SET_CURRENT_MAP_LAYER_MODE';
export function setCurrentMapLayerMode(currentMapLayerIndexView: string) {
  return {
    type: SET_CURRENT_MAP_LAYER_MODE,
    currentMapLayerIndexView,
  } as const;
}

export const SET_DATASET_SELECTION_MODAL_TOGGLE = 'SET_DATASET_SELECTION_MODAL_TOGGLE';
export function setDatasetSelectionModalToggle(toggle: boolean) {
  return {
    type: SET_DATASET_SELECTION_MODAL_TOGGLE,
    toggle,
  } as const;
}

export const SET_CUSTOM_COLOR_PALETTE = 'SET_CUSTOM_COLOR_PALETTE';
export function setCustomColorPalette(
  customColorPalette: ColorPalette,
  dimensionGroupingColumnName: string | null,
  seriesIndex: number,
) {
  return {
    type: SET_CUSTOM_COLOR_PALETTE,
    customColorPalette,
    dimensionGroupingColumnName,
    seriesIndex,
  } as const;
}

export const SET_CUSTOM_COLOR_PALETTE_WITH_QUANTIFICATION = 'SET_CUSTOM_COLOR_PALETTE_WITH_QUANTIFICATION';
export function setCustomColorPaletteWithQuantification(
  customColorPalette: ColorPalette,
  dimensionGroupingColumnName: string | null,
  seriesIndex: number,
  quantificationMethod: QUANTIFICATION_METHOD_VALUES,
) {
  return {
    type: SET_CUSTOM_COLOR_PALETTE_WITH_QUANTIFICATION,
    customColorPalette,
    dimensionGroupingColumnName,
    seriesIndex,
    quantificationMethod,
  } as const;
}

export const UPDATE_CUSTOM_COLOR_PALETTE = 'UPDATE_CUSTOM_COLOR_PALETTE';
export function updateCustomColorPalette(
  selectedColor: string,
  group: string | number,
  dimensionGroupingColumnName: string | null,
  seriesIndex?: number,
) {
  return {
    type: UPDATE_CUSTOM_COLOR_PALETTE,
    selectedColor,
    group,
    dimensionGroupingColumnName,
    seriesIndex,
  } as const;
}

export const UPDATE_CUSTOM_COLOR_PALETTE_WITH_QUANTIFICATION =
  'UPDATE_CUSTOM_COLOR_PALETTE_WITH_QUANTIFICATION';
export function updateCustomColorPaletteWithQuantification(
  selectedColor: string,
  group: string,
  dimensionGroupingColumnName: string | null,
  seriesIndex: number | undefined,
  quantificationMethod: QUANTIFICATION_METHOD_VALUES,
) {
  return {
    type: UPDATE_CUSTOM_COLOR_PALETTE_WITH_QUANTIFICATION,
    selectedColor,
    group,
    dimensionGroupingColumnName,
    seriesIndex,
    quantificationMethod,
  } as const;
}

export const UPDATE_CUSTOM_CHARM_NAME = 'UPDATE_CUSTOM_CHARM_NAME';
export function updateCustomCharmName(
  selectedCharmName: string,
  group: string,
  dimensionGroupingColumnName: string | null,
  seriesIndex?: number,
) {
  return {
    type: UPDATE_CUSTOM_CHARM_NAME,
    selectedCharmName,
    group,
    dimensionGroupingColumnName,
    seriesIndex,
  } as const;
}

export const UPDATE_CUSTOM_CHARM_NAME_WITH_QUANTIFICATION = 'UPDATE_CUSTOM_CHARM_NAME_WITH_QUANTIFICATION';
export function updateCustomCharmNameWithQuantification(
  selectedCharmName: string,
  group: string,
  dimensionGroupingColumnName: string | null,
  seriesIndex: number | undefined,
  quantificationMethod: QUANTIFICATION_METHOD_VALUES,
) {
  return {
    type: UPDATE_CUSTOM_CHARM_NAME_WITH_QUANTIFICATION,
    selectedCharmName,
    group,
    dimensionGroupingColumnName,
    seriesIndex,
    quantificationMethod,
  } as const;
}

export const SET_DATE_DISPLAY_FORMAT = 'SET_DATE_DISPLAY_FORMAT';
export function setDateDisplayFormat(format: string) {
  return {
    type: SET_DATE_DISPLAY_FORMAT,
    format,
  } as const;
}

export const SET_DIMENSION_GROUPING_COLUMN_NAME = 'SET_DIMENSION_GROUPING_COLUMN_NAME';
export function setDimensionGroupingColumnName(dimensionGroupingColumnName: string | null) {
  return {
    type: SET_DIMENSION_GROUPING_COLUMN_NAME,
    dimensionGroupingColumnName,
  } as const;
}

export const SET_ERROR_BARS_BAR_COLOR = 'SET_ERROR_BARS_BAR_COLOR';
export function setErrorBarsBarColor(color: string) {
  return {
    type: SET_ERROR_BARS_BAR_COLOR,
    color,
  } as const;
}

export const SET_ERROR_BARS_LOWER_BOUND_COLUMN_NAME = 'SET_ERROR_BARS_LOWER_BOUND_COLUMN_NAME';
export function setErrorBarsLowerBoundColumnName(columnName: string | null) {
  return {
    type: SET_ERROR_BARS_LOWER_BOUND_COLUMN_NAME,
    columnName,
  } as const;
}

export const SET_ERROR_BARS_UPPER_BOUND_COLUMN_NAME = 'SET_ERROR_BARS_UPPER_BOUND_COLUMN_NAME';
export function setErrorBarsUpperBoundColumnName(columnName: string | null) {
  return {
    type: SET_ERROR_BARS_UPPER_BOUND_COLUMN_NAME,
    columnName,
  } as const;
}

export const SET_GROUPING_ORDER_BY = 'SET_GROUPING_ORDER_BY';
export function setGroupingOrderBy(orderBy: any) {
  return {
    type: SET_GROUPING_ORDER_BY,
    orderBy,
  } as const;
}

export const SET_ORDER_BY = 'SET_ORDER_BY';
export function setOrderBy(orderBy: any) {
  return {
    type: SET_ORDER_BY,
    orderBy,
  } as const;
}

export const SET_COLUMN_ORDER_CONFIGURATION = 'SET_COLUMN_ORDER_CONFIGURATION';
export function setColumnOrderConfiguration(order: OrderConfig[]) {
  return {
    type: SET_COLUMN_ORDER_CONFIGURATION,
    order,
  } as const;
}

export const REPLACE_VIF = 'REPLACE_VIF';
export function replaceVif(updatedVif: Vif) {
  return {
    type: REPLACE_VIF,
    vif: updatedVif,
  } as const;
}

export const SET_DEFAULT_DISPLAY_DATE = 'SET_DEFAULT_DISPLAY_DATE';
export function setDefaultDisplayDate(defaultDisplayDate: DateInput) {
  return {
    type: SET_DEFAULT_DISPLAY_DATE,
    defaultDisplayDate,
  } as const;
}

export const SET_CURRENT_DISPLAY_DATE = 'SET_CURRENT_DISPLAY_DATE';
export function setCurrentDisplayDate(currentDisplayDate: DateInput) {
  return {
    type: SET_CURRENT_DISPLAY_DATE,
    currentDisplayDate,
  } as const;
}

export const SET_START_DATE_COLUMN = 'SET_START_DATE_COLUMN';
export function setStartDateColumn(startDateColumn: string | null) {
  return {
    type: SET_START_DATE_COLUMN,
    startDateColumn,
  } as const;
}

export const SET_END_DATE_COLUMN = 'SET_END_DATE_COLUMN';
export function setEndDateColumn(endDateColumn: string | null) {
  return {
    type: SET_END_DATE_COLUMN,
    endDateColumn,
  } as const;
}

export const SET_EVENT_TITLE_COLUMN = 'SET_EVENT_TITLE_COLUMN';
export function setEventTitleColumn(eventTitleColumn: string | null) {
  return {
    type: SET_EVENT_TITLE_COLUMN,
    eventTitleColumn,
  } as const;
}

export const SET_EVENT_BACKGROUND_COLOR = 'SET_EVENT_BACKGROUND_COLOR';
export function setEventBackgroundColor(eventBackgroundColor: string) {
  return {
    type: SET_EVENT_BACKGROUND_COLOR,
    eventBackgroundColor,
  } as const;
}

export const SET_EVENT_OUTLINE_COLOR = 'SET_EVENT_OUTLINE_COLOR';
export function setEventOutlineColor(eventOutlineColor: string) {
  return {
    type: SET_EVENT_OUTLINE_COLOR,
    eventOutlineColor,
  } as const;
}

export const SET_EVENT_TEXT_COLOR = 'SET_EVENT_TEXT_COLOR';
export function setEventTextColor(eventTextColor: string) {
  return {
    type: SET_EVENT_TEXT_COLOR,
    eventTextColor,
  } as const;
}

export const SET_PRECISION = 'SET_PRECISION';
export function setPrecision(precision: string | null) {
  return {
    type: SET_PRECISION,
    precision,
  } as const;
}

export const SET_STACKED = 'SET_STACKED';
export function setStacked({ stacked, oneHundredPercent }: { stacked: boolean; oneHundredPercent: boolean }) {
  return {
    type: SET_STACKED,
    stacked,
    oneHundredPercent,
  } as const;
}

export const SET_TREAT_NULL_VALUES_AS_ZERO = 'SET_TREAT_NULL_VALUES_AS_ZERO';
export function setTreatNullValuesAsZero(treatNullValuesAsZero: boolean) {
  return {
    type: SET_TREAT_NULL_VALUES_AS_ZERO,
    treatNullValuesAsZero,
  } as const;
}

export const SET_LIMIT_NONE_AND_SHOW_OTHER_CATEGORY = 'SET_LIMIT_NONE_AND_SHOW_OTHER_CATEGORY';
export function setLimitNoneAndShowOtherCategory() {
  return {
    type: SET_LIMIT_NONE_AND_SHOW_OTHER_CATEGORY,
    limitNone: true,
    showOtherCategory: false,
  } as const;
}

export const SET_LIMIT_COUNT_AND_SHOW_OTHER_CATEGORY = 'SET_LIMIT_COUNT_AND_SHOW_OTHER_CATEGORY';
export function setLimitCountAndShowOtherCategory(limitCount: number, showOtherCategory?: boolean) {
  return {
    type: SET_LIMIT_COUNT_AND_SHOW_OTHER_CATEGORY,
    limitCount,
    showOtherCategory,
  } as const;
}

export const SET_USE_SECONDARY_AXIS_FOR_COLUMNS = 'SET_USE_SECONDARY_AXIS_FOR_COLUMNS';
export function setUseSecondaryAxisForColumns(useSecondaryAxis: boolean) {
  return {
    type: SET_USE_SECONDARY_AXIS_FOR_COLUMNS,
    useSecondaryAxis,
  } as const;
}

export const SET_USE_SECONDARY_AXIS_FOR_LINES = 'SET_USE_SECONDARY_AXIS_FOR_LINES';
export function setUseSecondaryAxisForLines(useSecondaryAxis: boolean) {
  return {
    type: SET_USE_SECONDARY_AXIS_FOR_LINES,
    useSecondaryAxis,
  } as const;
}

export const SET_SHOW_OTHER_CATEGORY = 'SET_SHOW_OTHER_CATEGORY';
export function setShowOtherCategory(showOtherCategory: boolean) {
  return {
    type: SET_SHOW_OTHER_CATEGORY,
    showOtherCategory,
  } as const;
}

export const RESET_STATE = 'RESET_STATE';
export function resetState() {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    // Dispatching RESET_STATE to all reducers
    // Reducers will return initial states
    dispatch({ type: RESET_STATE });

    // Trigger load on empty VIF
    load(dispatch, getEmptyVif(state.vifAuthoring));
  };
}

export const SET_USER_ACTIVE = 'SET_USER_ACTIVE';
export function setUserActive() {
  return {
    type: SET_USER_ACTIVE,
  } as const;
}

export const SET_USER_IDLE = 'SET_USER_IDLE';
export function setUserIdle() {
  return {
    type: SET_USER_IDLE,
  } as const;
}

export const SET_MAP_TYPE = 'SET_MAP_TYPE';
export function setMapType(mapType: MAP_TYPES | string | null) {
  return {
    type: SET_MAP_TYPE,
    mapType,
  } as const;
}

export const SET_POINT_SIZE = 'SET_POINT_SIZE';
export function setPointSize(pointSize: number) {
  return {
    type: SET_POINT_SIZE,
    pointSize,
  } as const;
}

export const SET_POINT_OPACITY = 'SET_POINT_OPACITY';
export function setPointOpacity(pointOpacity: number | null) {
  return {
    type: SET_POINT_OPACITY,
    pointOpacity,
  } as const;
}

export const SET_RESIZE_POINTS_BY_COLUMN = 'SET_RESIZE_POINTS_BY_COLUMN';
export function setResizePointsByColumn(resizePointsBy: number | string | null) {
  return {
    type: SET_RESIZE_POINTS_BY_COLUMN,
    resizePointsBy,
  } as const;
}

export const SET_COLOR_BY_COLUMN = 'SET_COLOR_BY_COLUMN';
export function setColorByColumn(colorByColumn: string | null) {
  return {
    type: SET_COLOR_BY_COLUMN,
    colorByColumn,
  } as const;
}

export const SET_RESIZE_BY_COLUMN = 'SET_RESIZE_BY_COLUMN';
export function setResizeByColumn(resizeByColumn: string | null) {
  return {
    type: SET_RESIZE_BY_COLUMN,
    resizeByColumn,
  } as const;
}

export const SET_X_AXIS_COLUMN = 'SET_X_AXIS_COLUMN';
export function setXAxisColumn(xAxisColumn: string | null) {
  return {
    type: SET_X_AXIS_COLUMN,
    xAxisColumn,
  } as const;
}

export const SET_PLOT_SIZE = 'SET_PLOT_SIZE';
export function setPlotSize(plotSize: number) {
  return {
    type: SET_PLOT_SIZE,
    plotSize,
  } as const;
}

export const SET_Y_AXIS_COLUMN = 'SET_Y_AXIS_COLUMN';
export function setYAxisColumn(yAxisColumn: string | null) {
  return {
    type: SET_Y_AXIS_COLUMN,
    yAxisColumn,
  } as const;
}

export const SET_MINIMUM_PLOT_SIZE = 'SET_MINIMUM_PLOT_SIZE';
export function setMinimumPlotSize(minimumPlotSize: number) {
  return {
    type: SET_MINIMUM_PLOT_SIZE,
    minimumPlotSize,
  } as const;
}

export const SET_MAXIMUM_PLOT_SIZE = 'SET_MAXIMUM_PLOT_SIZE';
export function setMaximumPlotSize(maximumPlotSize: number) {
  return {
    type: SET_MAXIMUM_PLOT_SIZE,
    maximumPlotSize,
  } as const;
}

export const SET_MINIMUM_POINT_SIZE = 'SET_MINIMUM_POINT_SIZE';
export function setMinimumPointSize(minimumPointSize: number) {
  return {
    type: SET_MINIMUM_POINT_SIZE,
    minimumPointSize,
  } as const;
}

export const SET_MAXIMUM_POINT_SIZE = 'SET_MAXIMUM_POINT_SIZE';
export function setMaximumPointSize(maximumPointSize: number) {
  return {
    type: SET_MAXIMUM_POINT_SIZE,
    maximumPointSize,
  } as const;
}

export const SET_NUMBER_OF_DATA_CLASSES = 'SET_NUMBER_OF_DATA_CLASSES';
export function setNumberOfDataClasses(numberOfDataClasses: number) {
  return {
    type: SET_NUMBER_OF_DATA_CLASSES,
    numberOfDataClasses,
  } as const;
}

export const SET_COLOR_BY_BUCKETS_COUNT = 'SET_COLOR_BY_BUCKETS_COUNT';
export function setColorByBucketsCount(colorByBucketsCount: number) {
  return {
    type: SET_COLOR_BY_BUCKETS_COUNT,
    colorByBucketsCount,
  } as const;
}

export const SET_MID_POINT = 'SET_MID_POINT';
export function setMidpoint(midpoint: string) {
  return {
    type: SET_MID_POINT,
    midpoint,
  } as const;
}

export const SET_MAX_CLUSTERING_ZOOM_LEVEL = 'SET_MAX_CLUSTERING_ZOOM_LEVEL';
export function setMaxClusteringZoomLevel(maxClusteringZoomLevel: number) {
  return {
    type: SET_MAX_CLUSTERING_ZOOM_LEVEL,
    maxClusteringZoomLevel,
  } as const;
}

export const SET_POINT_THRESHOLD = 'SET_POINT_THRESHOLD';
export function setPointThreshold(pointThreshold: number) {
  return {
    type: SET_POINT_THRESHOLD,
    pointThreshold,
  } as const;
}

export const SET_CLUSTER_RADIUS = 'SET_CLUSTER_RADIUS';
export function setClusterRadius(clusterRadius: number) {
  return {
    type: SET_CLUSTER_RADIUS,
    clusterRadius,
  } as const;
}

export const SET_CAST_NULL_AS_FALSE_IN_SERIES = 'SET_CAST_NULL_AS_FALSE_IN_SERIES';
export function setCastNullAsFalseInSeries(castNullAsFalseInSeries: boolean) {
  return {
    type: SET_CAST_NULL_AS_FALSE_IN_SERIES,
    castNullAsFalseInSeries,
  } as const;
}

export const SET_MAX_CLUSTER_SIZE = 'SET_MAX_CLUSTER_SIZE';
export function setMaxClusterSize(maxClusterSize: number) {
  return {
    type: SET_MAX_CLUSTER_SIZE,
    maxClusterSize,
  } as const;
}

export const SET_STACK_RADIUS = 'SET_STACK_RADIUS';
export function setStackRadius(stackRadius: number) {
  return {
    type: SET_STACK_RADIUS,
    stackRadius,
  } as const;
}

export const SET_COLOR_POINTS_BY_COLUMN = 'SET_COLOR_POINTS_BY_COLUMN';
export function setColorPointsByColumn(colorPointsBy: string | null) {
  return {
    type: SET_COLOR_POINTS_BY_COLUMN,
    colorPointsBy,
  } as const;
}

export const SET_LINE_WEIGHT = 'SET_LINE_WEIGHT';
export function setLineWeight(lineWeight: number) {
  return {
    type: SET_LINE_WEIGHT,
    lineWeight,
  } as const;
}

export const SET_WEIGH_LINES_BY_COLUMN = 'SET_WEIGH_LINES_BY_COLUMN';
export function setWeighLinesByColumn(weighLinesBy: string | null) {
  return {
    type: SET_WEIGH_LINES_BY_COLUMN,
    weighLinesBy,
  } as const;
}

export const SET_MINIMUM_LINE_WEIGHT = 'SET_MINIMUM_LINE_WEIGHT';
export function setMinimumLineWeight(minimumLineWeight: number) {
  return {
    type: SET_MINIMUM_LINE_WEIGHT,
    minimumLineWeight,
  } as const;
}

export const SET_MAXIMUM_LINE_WEIGHT = 'SET_MAXIMUM_LINE_WEIGHT';
export function setMaximumLineWeight(maximumLineWeight: number) {
  return {
    type: SET_MAXIMUM_LINE_WEIGHT,
    maximumLineWeight,
  } as const;
}

export const SET_COLOR_LINES_BY_COLUMN = 'SET_COLOR_LINES_BY_COLUMN';
export function setColorLinesByColumn(colorLinesBy: string | null) {
  return {
    type: SET_COLOR_LINES_BY_COLUMN,
    colorLinesBy,
  } as const;
}

export const SET_BOUNDARY_COLOR_BY_COLUMN = 'SET_BOUNDARY_COLOR_BY_COLUMN';
export function setBoundaryColorByColumn(colorBoundariesBy: string | null) {
  return {
    type: SET_BOUNDARY_COLOR_BY_COLUMN,
    colorBoundariesBy,
  } as const;
}

export const SET_COLOR_BY_QUANTIFICATION_METHOD = 'SET_COLOR_BY_QUANTIFICATION_METHOD';
export function setColorByQuantificationMethod(
  colorByQuantificationMethod: QUANTIFICATION_METHOD_VALUES | 'category' | 'linear',
) {
  return {
    type: SET_COLOR_BY_QUANTIFICATION_METHOD,
    colorByQuantificationMethod,
  } as const;
}

export const SET_RANGE_BUCKET_TYPE = 'SET_RANGE_BUCKET_TYPE';
export function setRangeBucketType(rangeBucketType: string) {
  return {
    type: SET_RANGE_BUCKET_TYPE,
    rangeBucketType,
  } as const;
}

export const SET_POINT_AGGREGATION = 'SET_POINT_AGGREGATION';
export function setPointAggregation(pointAggregation: 'heat_map' | 'region_map' | string) {
  return {
    type: SET_POINT_AGGREGATION,
    pointAggregation,
  } as const;
}

export const SET_BASEMAP_STYLE = 'SET_BASEMAP_STYLE';
export function setBasemapStyle(basemapStyle: string) {
  return {
    type: SET_BASEMAP_STYLE,
    basemapStyle,
  } as const;
}

export const SET_BASEMAP_OPACITY = 'SET_BASEMAP_OPACITY';
export function setBasemapOpacity(basemapOpacity: number) {
  return {
    type: SET_BASEMAP_OPACITY,
    basemapOpacity,
  } as const;
}

export const SET_NAVIGATION_CONTROL = 'SET_NAVIGATION_CONTROL';
export function setNavigationControl(navigationControl: boolean) {
  return {
    type: SET_NAVIGATION_CONTROL,
    navigationControl,
  } as const;
}

export const SET_LAYER_TOGGLE_CONTROL = 'SET_LAYER_TOGGLE_CONTROL';
export function setLayerToggleControl(layerToggleControl: boolean) {
  return {
    type: SET_LAYER_TOGGLE_CONTROL,
    layerToggleControl,
  } as const;
}

export const SET_GEO_CODER_CONTROL = 'SET_GEO_CODER_CONTROL';
export function setGeoCoderControl(geoCoderControl: boolean) {
  return {
    type: SET_GEO_CODER_CONTROL,
    geoCoderControl,
  } as const;
}

export const SET_SHOW_DATA_TABLE_CONTROL = 'SET_SHOW_DATA_TABLE_CONTROL';
export function setShowDataTableControl(showDataTableControl: boolean) {
  return {
    type: SET_SHOW_DATA_TABLE_CONTROL,
    showDataTableControl,
  } as const;
}

export const SET_LOCK_CALENDAR_VIEW_CONTROL = 'SET_LOCK_CALENDAR_VIEW_CONTROL';
export function setLockCalendarViewControl(lockCalendarViewControl: boolean) {
  return {
    type: SET_LOCK_CALENDAR_VIEW_CONTROL,
    lockCalendarViewControl,
  } as const;
}

export const SET_GEO_LOCATE_CONTROL = 'SET_GEO_LOCATE_CONTROL';
export function setGeoLocateControl(geoLocateControl: boolean) {
  return {
    type: SET_GEO_LOCATE_CONTROL,
    geoLocateControl,
  } as const;
}

export const SET_MAP_FLYOUT_TITLE_COLUMN_NAME = 'SET_MAP_FLYOUT_TITLE_COLUMN_NAME';
export function setMapFlyoutTitleColumnName(mapFlyoutTitleColumnName: string | null) {
  return {
    type: SET_MAP_FLYOUT_TITLE_COLUMN_NAME,
    mapFlyoutTitleColumnName,
  } as const;
}

export const ADD_DRILL_DOWN_COLUMN = 'ADD_DRILL_DOWN_COLUMN';
export function addDrilldownColumn(drilldownColumnName: string) {
  return {
    type: ADD_DRILL_DOWN_COLUMN,
    drilldownColumnName,
  } as const;
}

export const REMOVE_DRILL_DOWN_COLUMN = 'REMOVE_DRILL_DOWN_COLUMN';
export function removeDrilldownColumn(index: number) {
  return {
    type: REMOVE_DRILL_DOWN_COLUMN,
    index,
  } as const;
}

export const CHANGE_DRILL_DOWN_COLUMN = 'CHANGE_DRILL_DOWN_COLUMN';
export function changeDrilldownColumn(index: number, columnName: string) {
  return {
    type: CHANGE_DRILL_DOWN_COLUMN,
    columnName,
    index,
  } as const;
}

export const ADD_TABLE_COLUMN = 'ADD_TABLE_COLUMN';
export function addTableColumn(tableColumnName: string, columns: ViewColumn[]) {
  return {
    type: ADD_TABLE_COLUMN,
    tableColumnName,
    columns,
  } as const;
}

export const REMOVE_TABLE_COLUMN = 'REMOVE_TABLE_COLUMN';
export function removeTableColumn(index: number) {
  return {
    type: REMOVE_TABLE_COLUMN,
    index,
  } as const;
}

export const REPLACE_ALL_COLUMNS = 'REPLACE_ALL_COLUMNS';
export function replaceAllTableColumns(columns: ViewColumn[]) {
  return {
    type: REPLACE_ALL_COLUMNS,
    columns,
  } as const;
}

export const RESET_ALL_COLUMNS = 'RESET_ALL_COLUMNS';
export function resetAllColumns() {
  return {
    type: RESET_ALL_COLUMNS,
  } as const;
}

export const REORDER_TABLE_COLUMNS = 'REORDER_TABLE_COLUMNS';
export function reorderTableColumns(newOrder: ViewColumn[]) {
  return {
    type: REORDER_TABLE_COLUMNS,
    newOrder,
  } as const;
}

export const NEW_TABLE_HIERARCHY = 'NEW_TABLE_HIERARCHY';
export function newTableHierarchy() {
  return {
    type: NEW_TABLE_HIERARCHY,
  } as const;
}

export const UPDATE_TABLE_VIZ_COLUMN_FORMAT = 'UPDATE_TABLE_VIZ_COLUMN_FORMAT';
export function updateTableVisualizationColumnFormat(
  updatedColumnFormat: TableColumnFormat,
  columnName: string,
) {
  return {
    updatedColumnFormat,
    columnName,
    type: UPDATE_TABLE_VIZ_COLUMN_FORMAT,
  } as const;
}

export const UPDATE_TABLE_VIZ_COLUMN_STYLE = 'UPDATE_TABLE_VIZ_COLUMN_STYLE';
export function updateTableVisualizationColumnStyle(
  updatedColumnFormatStyle: FormatStyle,
  columnName: string,
) {
  return {
    updatedColumnFormatStyle,
    columnName,
    type: UPDATE_TABLE_VIZ_COLUMN_STYLE,
  } as const;
}

export const UPDATE_TABLE_VIZ_COLUMN_STYLE_CONDITION = 'UPDATE_TABLE_VIZ_COLUMN_STYLE_CONDITION';
export function updateTableVisualizationColumnStyleCondition(
  updatedColumnFormatStyle: FormatStyle,
  columnName: string,
) {
  return {
    updatedColumnFormatStyle,
    columnName,
    type: UPDATE_TABLE_VIZ_COLUMN_STYLE_CONDITION,
  } as const;
}

export const UPDATE_TABLE_VIZ_HEADER_STYLE = 'UPDATE_TABLE_VIZ_HEADER_STYLE';
export function updateTableVisualizationHeaderStyle(updatedHeaderFormatStyle: HeaderFormatStyle) {
  return {
    updatedHeaderFormatStyle,
    type: UPDATE_TABLE_VIZ_HEADER_STYLE,
  } as const;
}

export const UPDATE_TABLE_VIZ_HEADER_TEXT_STYLE = 'UPDATE_TABLE_VIZ_HEADER_TEXT_STYLE';
export function updateTableVisualizationHeaderTextStyle(updatedHeaderFormatTextStyle: FontStyle) {
  return {
    updatedHeaderFormatTextStyle,
    type: UPDATE_TABLE_VIZ_HEADER_TEXT_STYLE,
  } as const;
}

export const SET_ACTIVE_HIERARCHY_ID = 'SET_ACTIVE_HIERARCHY_ID';
export function setActiveHierarchyId(hierarchyId: string) {
  return {
    type: SET_ACTIVE_HIERARCHY_ID,
    hierarchyId,
  } as const;
}

export const SHOW_GRAND_TOTAL = 'SHOW_GRAND_TOTAL';
export function showGrandTotal(hierarchyIndex: number, isShowing: boolean) {
  return {
    type: SHOW_GRAND_TOTAL,
    hierarchyIndex,
    isShowing,
  } as const;
}

export const SHOW_SUB_TOTAL = 'SHOW_SUB_TOTAL';
export function showSubTotal(hierarchyIndex: number, isShowing: boolean) {
  return {
    type: SHOW_SUB_TOTAL,
    hierarchyIndex,
    isShowing,
  } as const;
}

export const UPDATE_CUSTOM_LABELS = 'UPDATE_CUSTOM_LABELS';
export function updateCustomLabels(hierarchyIndex: number, customLabels: Partial<CustomLabels>) {
  return {
    type: UPDATE_CUSTOM_LABELS,
    hierarchyIndex,
    customLabels,
  } as const;
}

export const APPLY_ADVANCED_TOTALING_CONFIG = 'APPLY_ADVANCED_TOTALING_CONFIG';
export function applyAdvancedTotalingConfig(
  hierarchyIndex: number,
  config: {
    subtotalLabelForColumn: { columnName: string; subtotalLabel: string };
    customFormulas: Record<string, string>;
  },
) {
  return {
    type: APPLY_ADVANCED_TOTALING_CONFIG,
    hierarchyIndex,
    ...config,
  } as const;
}

export const UPDATE_TABLE_HIERARCHY_NAME = 'UPDATE_TABLE_HIERARCHY_NAME';
export function updateTableHierarchyName(hierarchyIndex: number, name: string) {
  return {
    type: UPDATE_TABLE_HIERARCHY_NAME,
    hierarchyIndex,
    name,
  } as const;
}

export const DUPLICATE_TABLE_HIERARCHY = 'DUPLICATE_TABLE_HIERARCHY';
export function duplicateTableHierarchy(index: number) {
  return {
    type: DUPLICATE_TABLE_HIERARCHY,
    id: Math.random().toString(36).slice(2),
    index,
  } as const;
}

export const REMOVE_TABLE_HIERARCHY = 'REMOVE_TABLE_HIERARCHY';
export function removeTableHierarchy(index: number) {
  return {
    type: REMOVE_TABLE_HIERARCHY,
    index,
  } as const;
}

export const ADD_BASEMAP_FLYOUT_COLUMN = 'ADD_BASEMAP_FLYOUT_COLUMN';
export function addBasemapFlyoutColumn(columnName: string) {
  return {
    type: ADD_BASEMAP_FLYOUT_COLUMN,
    columnName,
  } as const;
}

export const REMOVE_BASEMAP_FLYOUT_COLUMN = 'REMOVE_BASEMAP_FLYOUT_COLUMN';
export function removeBasemapFlyoutColumn(relativeIndex: number) {
  return {
    type: REMOVE_BASEMAP_FLYOUT_COLUMN,
    relativeIndex,
  } as const;
}

export const CHANGE_ADDITIONAL_FLYOUT_COLUMN = 'CHANGE_ADDITIONAL_FLYOUT_COLUMN';
export function changeAdditionalFlyoutColumn(relativeIndex: number, columnName: string) {
  return {
    type: CHANGE_ADDITIONAL_FLYOUT_COLUMN,
    columnName,
    relativeIndex,
  } as const;
}

export const SET_SHOW_ANNOTATIONS_IN_LEGEND = 'SET_SHOW_ANNOTATIONS_IN_LEGEND';
export function setShowAnnotationsInLegend(showAnnotationsInLegend: boolean) {
  return {
    type: SET_SHOW_ANNOTATIONS_IN_LEGEND,
    showAnnotationsInLegend,
  } as const;
}

/**
 * `date`/`description` are nullable because the UI lets the user add an annotation before filling
 * it in; the persisted Vif's `Annotation` fields are non-nullable, since an incomplete annotation
 * is never saved.
 */
type AnnotationInput = { date: string | null; description: string | null };

export const SET_ANNOTATIONS = 'SET_ANNOTATIONS';
export function setAnnotations(annotations: AnnotationInput[]) {
  return {
    type: SET_ANNOTATIONS,
    annotations,
  } as const;
}

export const ADD_ANNOTATION = 'ADD_ANNOTATION';
export function addAnnotation(annotation: AnnotationInput) {
  return {
    type: ADD_ANNOTATION,
    annotation,
  } as const;
}

export const REMOVE_ANNOTATION = 'REMOVE_ANNOTATION';
export function removeAnnotation(index: number) {
  return {
    type: REMOVE_ANNOTATION,
    index,
  } as const;
}

export const UPDATE_ANNOTATION = 'UPDATE_ANNOTATION';
export function updateAnnotation(index: number, annotation: AnnotationInput) {
  return {
    type: UPDATE_ANNOTATION,
    annotation,
    index,
  } as const;
}

export const ADD_REGIONMAP_FLYOUT_COLUMN_AND_AGGREGATION = 'ADD_REGIONMAP_FLYOUT_COLUMN_AND_AGGREGATION';
export function addRegionMapFlyoutColumnAndAggregation(columnAndAggregation: ColumnAndAggregation) {
  return {
    type: ADD_REGIONMAP_FLYOUT_COLUMN_AND_AGGREGATION,
    columnAndAggregation,
  } as const;
}

export const REMOVE_REGIONMAP_FLYOUT_COLUMN_AND_AGGREGATION =
  'REMOVE_REGIONMAP_FLYOUT_COLUMN_AND_AGGREGATION';
export function removeRegionMapFlyoutColumnAndAggregation(index: number) {
  return {
    type: REMOVE_REGIONMAP_FLYOUT_COLUMN_AND_AGGREGATION,
    index,
  } as const;
}

export const CHANGE_REGIONMAP_FLYOUT_COLUMN_AND_AGGREGATION =
  'CHANGE_REGIONMAP_FLYOUT_COLUMN_AND_AGGREGATION';
export function changeRegionMapFlyoutColumnAndAggregation(
  index: number,
  columnAndAggregation: ColumnAndAggregation,
) {
  return {
    type: CHANGE_REGIONMAP_FLYOUT_COLUMN_AND_AGGREGATION,
    columnAndAggregation,
    index,
  } as const;
}

export const SET_ADDITIONAL_FLYOUT_COLUMNS = 'SET_ADDITIONAL_FLYOUT_COLUMNS';
export function setAdditionalFlyoutColumns(columns: string[]) {
  return {
    type: SET_ADDITIONAL_FLYOUT_COLUMNS,
    columns,
  } as const;
}

export const SET_REGIONMAP_FLYOUT_COLUMN_AND_AGGREGATION = 'SET_REGIONMAP_FLYOUT_COLUMN_AND_AGGREGATION';
export function setRegionMapFlyoutColumnAndAggregations(columnAndAggregation: ColumnAndAggregation[]) {
  return {
    type: SET_REGIONMAP_FLYOUT_COLUMN_AND_AGGREGATION,
    columnAndAggregation,
  } as const;
}

export const SET_SEARCH_BOUNDARY_UPPER_LEFT_LATITUDE = 'SET_SEARCH_BOUNDARY_UPPER_LEFT_LATITUDE';
export function setSearchBoundaryUpperLeftLatitude(searchBoundaryUpperLeftLatitude: number) {
  return {
    type: SET_SEARCH_BOUNDARY_UPPER_LEFT_LATITUDE,
    searchBoundaryUpperLeftLatitude,
  } as const;
}

export const SET_SEARCH_BOUNDARY_UPPER_LEFT_LONGITUDE = 'SET_SEARCH_BOUNDARY_UPPER_LEFT_LONGITUDE';
export function setSearchBoundaryUpperLeftLongitude(searchBoundaryUpperLeftLongitude: number) {
  return {
    type: SET_SEARCH_BOUNDARY_UPPER_LEFT_LONGITUDE,
    searchBoundaryUpperLeftLongitude,
  } as const;
}

export const SET_SEARCH_BOUNDARY_LOWER_RIGHT_LATITUDE = 'SET_SEARCH_BOUNDARY_LOWER_RIGHT_LATITUDE';
export function setSearchBoundaryLowerRightLatitude(searchBoundaryLowerRightLatitude: number) {
  return {
    type: SET_SEARCH_BOUNDARY_LOWER_RIGHT_LATITUDE,
    searchBoundaryLowerRightLatitude,
  } as const;
}

export const SET_SEARCH_BOUNDARY_LOWER_RIGHT_LONGITUDE = 'SET_SEARCH_BOUNDARY_LOWER_RIGHT_LONGITUDE';
export function setSearchBoundaryLowerRightLongitude(searchBoundaryLowerRightLongitude: number) {
  return {
    type: SET_SEARCH_BOUNDARY_LOWER_RIGHT_LONGITUDE,
    searchBoundaryLowerRightLongitude,
  } as const;
}

export const SET_PITCH_AND_BEARING = 'SET_PITCH_AND_BEARING';
export function setPitchAndBearing(pitchAndBearing: MapPitchAndBearing) {
  return {
    type: SET_PITCH_AND_BEARING,
    pitchAndBearing,
  } as const;
}

export const SET_MAP_ZOOM_LEVEL = 'SET_MAP_ZOOM_LEVEL';
export function setMapZoomLevel(mapZoomLevel: { start: number; end: number }) {
  return {
    type: SET_MAP_ZOOM_LEVEL,
    mapZoomLevel,
  } as const;
}

export const SET_SHAPE_FILL_COLOR = 'SET_SHAPE_FILL_COLOR';
export function setShapeFillColor(shapeFillColor: string) {
  return {
    type: SET_SHAPE_FILL_COLOR,
    shapeFillColor,
  } as const;
}

export const SET_SHAPE_FILL_OPACITY = 'SET_SHAPE_FILL_OPACITY';
export function setShapeFillOpacity(shapeFillOpacity: string | number) {
  return {
    type: SET_SHAPE_FILL_OPACITY,
    shapeFillOpacity,
  } as const;
}

export const SET_LINE_COLOR_OPACITY = 'SET_LINE_COLOR_OPACITY';
export function setLineColorOpacity(lineColorOpacity: string | number) {
  return {
    type: SET_LINE_COLOR_OPACITY,
    lineColorOpacity,
  } as const;
}

export const SET_LINE_STYLE_POINTS = 'SET_LINE_STYLE_POINTS';
export function setLineStylePoints(points: string | null) {
  return {
    type: SET_LINE_STYLE_POINTS,
    points,
  } as const;
}

export const SET_LINE_STYLE_POINT_RADIUS = 'SET_LINE_STYLE_POINT_RADIUS';
export function setLineStylePointRadius(pointRadius: number | null) {
  return {
    type: SET_LINE_STYLE_POINT_RADIUS,
    pointRadius,
  } as const;
}

export const SET_SHAPE_OUTLINE_COLOR = 'SET_SHAPE_OUTLINE_COLOR';
export function setShapeOutlineColor(shapeOutlineColor: string) {
  return {
    type: SET_SHAPE_OUTLINE_COLOR,
    shapeOutlineColor,
  } as const;
}

export const SET_SHAPE_OUTLINE_WIDTH = 'SET_SHAPE_OUTLINE_WIDTH';
export function setShapeOutlineWidth(shapeOutlineWidth: string | number) {
  return {
    type: SET_SHAPE_OUTLINE_WIDTH,
    shapeOutlineWidth,
  } as const;
}

export const TRIGGER_AUTO_SELECT_GEO_LOCATION_COLUMN = 'TRIGGER_AUTO_SELECT_GEO_LOCATION_COLUMN';
export function setTriggerAutoSelectGeoLocationColumn(toggle: boolean) {
  return {
    type: TRIGGER_AUTO_SELECT_GEO_LOCATION_COLUMN,
    toggle,
  } as const;
}

export const SET_HIDE_DUPLICATE_GROUPS = 'SET_HIDE_DUPLICATE_GROUPS';
export function setHideDuplicateGroups(toggle: boolean) {
  return {
    type: SET_HIDE_DUPLICATE_GROUPS,
    toggle,
  } as const;
}

export const SET_TABLE_INDENTED_LAYOUT = 'SET_TABLE_INDENTED_LAYOUT';
export function setTableIndentedLayout(toggle: boolean) {
  return {
    type: SET_TABLE_INDENTED_LAYOUT,
    toggle,
  } as const;
}

export const SET_TABLE_ROW_STRIPING = 'SET_TABLE_ROW_STRIPING';
export function setTableRowStripeStyleAction(tableRowStripeStyle: RowStripeStyle) {
  return {
    type: SET_TABLE_ROW_STRIPING,
    tableRowStripeStyle,
  } as const;
}

export const SET_TABLE_CUSTOM_SORT = 'SET_TABLE_CUSTOM_SORT';
export function setTableCustomSort(
  hierarchyId: string | undefined,
  columnName: string | null,
  rowData: unknown[],
) {
  return {
    type: SET_TABLE_CUSTOM_SORT,
    hierarchyId,
    columnName,
    rowData,
  } as const;
}

export const SET_TABLE_ORDER_CONFIG = 'SET_TABLE_ORDER_CONFIG';
export function setTableOrderConfig(orderConfig: OrderConfig[], hierarchyId: string | undefined) {
  return {
    type: SET_TABLE_ORDER_CONFIG,
    orderConfig,
    hierarchyId,
  } as const;
}

export const SET_TABLE_HEADER_SORT_CYCLE_INDEX = 'SET_TABLE_HEADER_SORT_CYCLE_INDEX';
export function setTableHeaderSortCycleIndex(headerSortCycleIndex: number, hierarchyId: string | undefined) {
  return {
    type: SET_TABLE_HEADER_SORT_CYCLE_INDEX,
    headerSortCycleIndex,
    hierarchyId,
  } as const;
}

export const SET_SHOW_SUMMARY_TABLE = 'SET_SHOW_SUMMARY_TABLE';
export function setShowSummaryTable(showSummaryTable: boolean) {
  return {
    type: SET_SHOW_SUMMARY_TABLE,
    showSummaryTable,
  } as const;
}

/**
 * Handles setting the custom color palette of various viz types. This function
 * will use info in the Vif to determine how to set the custom color palette in
 * the vif. If a quantification method is set, it will store the custom palette
 * under series[0].color.customPalette.COLUMN_NAME.QUANTIFICATION_METHOD rather
 * than series[0].color.customPalette.COLUMN_NAME.
 */
const setCustomColorPaletteForAllVizType = ({
  colorPaletteGroupingColumnName,
  customColorPalette,
  currentSeriesIndex,
  dimensionColumnName,
  dispatch,
  vifAuthoring,
  quantificationMethod,
}: {
  colorPaletteGroupingColumnName: string | null;
  customColorPalette: ColorPalette;
  currentSeriesIndex: number;
  dimensionColumnName: string;
  dispatch: Dispatch;
  vifAuthoring: VifAuthoring;
  quantificationMethod: QUANTIFICATION_METHOD_VALUES;
}) => {
  if (!_.isNull(colorPaletteGroupingColumnName)) {
    if (isMap(vifAuthoring) && quantificationMethod) {
      dispatch(
        setCustomColorPaletteWithQuantification(
          customColorPalette,
          colorPaletteGroupingColumnName,
          currentSeriesIndex,
          quantificationMethod,
        ),
      );
    }
    if (isPieChart(vifAuthoring)) {
      // For pie charts, we need to ensure all series (including flyouts) get the updates to color palettes
      const series = getSeries(vifAuthoring);
      _.forEach(series, (s, index) => {
        dispatch(setCustomColorPalette(customColorPalette, colorPaletteGroupingColumnName, index));
      });
    } else {
      dispatch(setCustomColorPalette(customColorPalette, colorPaletteGroupingColumnName, currentSeriesIndex));
    }
  } else {
    if (isMap(vifAuthoring) && quantificationMethod) {
      dispatch(
        setCustomColorPaletteWithQuantification(
          customColorPalette,
          dimensionColumnName,
          currentSeriesIndex,
          quantificationMethod,
        ),
      );
    } else {
      dispatch(setCustomColorPalette(customColorPalette, dimensionColumnName, currentSeriesIndex));
    }
  }
};

/**
 * Every plain (non-thunk) action this file can dispatch. Excludes the thunk action creators
 * (`setDataSource`, `setCuratedRegions`, `setComputedRegionColumns`, `initiateRegionCoding`,
 * `setColorPaletteProperties`, `requestShapefileMetadata`, `resetState`), which return a function
 * rather than a plain `{ type, ... }` object. Includes the two bare `{ type }` actions dispatched
 * directly from inside `resetState` and `setColorPaletteProperties`, which have no dedicated
 * action creator of their own.
 */
export type Action =
  | { type: typeof RESET_STATE }
  | { type: typeof SET_COLOR_PALETTE_PROPERTIES }
  | ReturnType<typeof setVifCheckpoint>
  | ReturnType<typeof requestMetadata>
  | ReturnType<typeof receiveMetadata>
  | ReturnType<typeof handleMetadataError>
  | ReturnType<typeof requestCuratedRegions>
  | ReturnType<typeof receiveCuratedRegions>
  | ReturnType<typeof handleCuratedRegionsError>
  | ReturnType<typeof requestComputedRegionColumns>
  | ReturnType<typeof receiveComputedRegionColumns>
  | ReturnType<typeof handleComputedRegionColumnsError>
  | ReturnType<typeof setDatasetUid>
  | ReturnType<typeof setDomain>
  | ReturnType<typeof setMapLayerName>
  | ReturnType<typeof setMapLayerSource>
  | ReturnType<typeof setMapLayerVisible>
  | ReturnType<typeof setSimplificationLevel>
  | ReturnType<typeof setCharmName>
  | ReturnType<typeof setFilters>
  | ReturnType<typeof setTableColumns>
  | ReturnType<typeof setTableHierarchies>
  | ReturnType<typeof setTableVisualizationColumnFormats>
  | ReturnType<typeof setDimension>
  | ReturnType<typeof setCurrentDrilldownColumnName>
  | ReturnType<typeof setDrilldowns>
  | ReturnType<typeof appendSeries>
  | ReturnType<typeof removeSeries>
  | ReturnType<typeof removeMetadataSeries>
  | ReturnType<typeof swapSeries>
  | ReturnType<typeof swapColorPalette>
  | ReturnType<typeof swapColorPaletteWithQuantification>
  | ReturnType<typeof swapMetaDataCollectionSeries>
  | ReturnType<typeof setSeriesVariant>
  | ReturnType<typeof setMeasureAggregation>
  | ReturnType<typeof setMeasureColumn>
  | ReturnType<typeof appendReferenceLine>
  | ReturnType<typeof removeReferenceLine>
  | ReturnType<typeof setReferenceLineColor>
  | ReturnType<typeof setReferenceLineLabel>
  | ReturnType<typeof setReferenceLineValue>
  | ReturnType<typeof setVisualizationType>
  | ReturnType<typeof requestRegionCoding>
  | ReturnType<typeof setRegionCodingShapefileId>
  | ReturnType<typeof awaitRegionCoding>
  | ReturnType<typeof finishRegionCoding>
  | ReturnType<typeof hideRegionCodingError>
  | ReturnType<typeof handleRegionCodingError>
  | ReturnType<typeof setComputedColumn>
  | ReturnType<typeof setComputedColumnForShapeFile>
  | ReturnType<typeof requestShapefile>
  | ReturnType<typeof setShapefile>
  | ReturnType<typeof setShapefileUid>
  | ReturnType<typeof setShapefilePrimaryKey>
  | ReturnType<typeof setShapefileGeometryLabel>
  | ReturnType<typeof setTitle>
  | ReturnType<typeof setDescription>
  | ReturnType<typeof setViewSourceDataLink>
  | ReturnType<typeof setShowTableActions>
  | ReturnType<typeof setShowExistingActionColumn>
  | ReturnType<typeof setSelectedExistingColumn>
  | ReturnType<typeof setSelectedActionForExistingColumn>
  | ReturnType<typeof setNewTableActions>
  | ReturnType<typeof setTableActionsMediaType>
  | ReturnType<typeof setTableActionsContextFieldsMapping>
  | ReturnType<typeof setPrimaryColor>
  | ReturnType<typeof setSecondaryColor>
  | ReturnType<typeof setColorScale>
  | ReturnType<typeof handleCustomColorPaletteError>
  | ReturnType<typeof setColorPalette>
  | ReturnType<typeof setNegativeColor>
  | ReturnType<typeof setZeroColor>
  | ReturnType<typeof setPositiveColor>
  | ReturnType<typeof setBaseLayer>
  | ReturnType<typeof setBaseLayerOpacity>
  | ReturnType<typeof setLabelTop>
  | ReturnType<typeof setLabelLeft>
  | ReturnType<typeof setLabelBottom>
  | ReturnType<typeof setLabelRight>
  | ReturnType<typeof setLegendPosition>
  | ReturnType<typeof setShowDimensionLabels>
  | ReturnType<typeof setShowLineValueLabels>
  | ReturnType<typeof setShowNullsAsFalse>
  | ReturnType<typeof setShowValueLabels>
  | ReturnType<typeof setShowValueLabelsAsPercent>
  | ReturnType<typeof setShowLegend>
  | ReturnType<typeof setShowMultiplePointsSymbolInLegend>
  | ReturnType<typeof setShowLegendForMap>
  | ReturnType<typeof setShowLegendOpened>
  | ReturnType<typeof setWrapDimensionLabels>
  | ReturnType<typeof setXAxisScalingMode>
  | ReturnType<typeof setMeasureAxisMaxValue>
  | ReturnType<typeof setMeasureAxisMinValue>
  | ReturnType<typeof setMeasureAxisPrecision>
  | ReturnType<typeof setLogarithmicScale>
  | ReturnType<typeof setMapLegendPrecision>
  | ReturnType<typeof setMapFlyoutPrecision>
  | ReturnType<typeof setMeasureAxisScale>
  | ReturnType<typeof setSecondaryMeasureAxisMaxValue>
  | ReturnType<typeof setSecondaryMeasureAxisMinValue>
  | ReturnType<typeof setSecondaryMeasureAxisPrecision>
  | ReturnType<typeof setSecondaryMeasureAxisScale>
  | ReturnType<typeof setUnitsOne>
  | ReturnType<typeof setUnitsOther>
  | ReturnType<typeof setRowInspectorTitleColumnName>
  | ReturnType<typeof setShowSlicePercentsInFlyouts>
  | ReturnType<typeof setDimensionLabelAreaSize>
  | ReturnType<typeof setDimensionLabelRotationAngle>
  | ReturnType<typeof setCenterAndZoom>
  | ReturnType<typeof setMapInfoDismissed>
  | ReturnType<typeof setCurrentMapLayerIndex>
  | ReturnType<typeof setLayerAsPrimary>
  | ReturnType<typeof setCurrentMapLayerMode>
  | ReturnType<typeof setDatasetSelectionModalToggle>
  | ReturnType<typeof setCustomColorPalette>
  | ReturnType<typeof setCustomColorPaletteWithQuantification>
  | ReturnType<typeof updateCustomColorPalette>
  | ReturnType<typeof updateCustomColorPaletteWithQuantification>
  | ReturnType<typeof updateCustomCharmName>
  | ReturnType<typeof updateCustomCharmNameWithQuantification>
  | ReturnType<typeof setDateDisplayFormat>
  | ReturnType<typeof setDimensionGroupingColumnName>
  | ReturnType<typeof setErrorBarsBarColor>
  | ReturnType<typeof setErrorBarsLowerBoundColumnName>
  | ReturnType<typeof setErrorBarsUpperBoundColumnName>
  | ReturnType<typeof setGroupingOrderBy>
  | ReturnType<typeof setOrderBy>
  | ReturnType<typeof setColumnOrderConfiguration>
  | ReturnType<typeof replaceVif>
  | ReturnType<typeof setDefaultDisplayDate>
  | ReturnType<typeof setCurrentDisplayDate>
  | ReturnType<typeof setStartDateColumn>
  | ReturnType<typeof setEndDateColumn>
  | ReturnType<typeof setEventTitleColumn>
  | ReturnType<typeof setEventBackgroundColor>
  | ReturnType<typeof setEventOutlineColor>
  | ReturnType<typeof setEventTextColor>
  | ReturnType<typeof setPrecision>
  | ReturnType<typeof setStacked>
  | ReturnType<typeof setTreatNullValuesAsZero>
  | ReturnType<typeof setLimitNoneAndShowOtherCategory>
  | ReturnType<typeof setLimitCountAndShowOtherCategory>
  | ReturnType<typeof setUseSecondaryAxisForColumns>
  | ReturnType<typeof setUseSecondaryAxisForLines>
  | ReturnType<typeof setShowOtherCategory>
  | ReturnType<typeof setUserActive>
  | ReturnType<typeof setUserIdle>
  | ReturnType<typeof setMapType>
  | ReturnType<typeof setPointSize>
  | ReturnType<typeof setPointOpacity>
  | ReturnType<typeof setResizePointsByColumn>
  | ReturnType<typeof setColorByColumn>
  | ReturnType<typeof setResizeByColumn>
  | ReturnType<typeof setXAxisColumn>
  | ReturnType<typeof setPlotSize>
  | ReturnType<typeof setYAxisColumn>
  | ReturnType<typeof setMinimumPlotSize>
  | ReturnType<typeof setMaximumPlotSize>
  | ReturnType<typeof setMinimumPointSize>
  | ReturnType<typeof setMaximumPointSize>
  | ReturnType<typeof setNumberOfDataClasses>
  | ReturnType<typeof setColorByBucketsCount>
  | ReturnType<typeof setMidpoint>
  | ReturnType<typeof setMaxClusteringZoomLevel>
  | ReturnType<typeof setPointThreshold>
  | ReturnType<typeof setClusterRadius>
  | ReturnType<typeof setCastNullAsFalseInSeries>
  | ReturnType<typeof setMaxClusterSize>
  | ReturnType<typeof setStackRadius>
  | ReturnType<typeof setColorPointsByColumn>
  | ReturnType<typeof setLineWeight>
  | ReturnType<typeof setWeighLinesByColumn>
  | ReturnType<typeof setMinimumLineWeight>
  | ReturnType<typeof setMaximumLineWeight>
  | ReturnType<typeof setColorLinesByColumn>
  | ReturnType<typeof setBoundaryColorByColumn>
  | ReturnType<typeof setColorByQuantificationMethod>
  | ReturnType<typeof setRangeBucketType>
  | ReturnType<typeof setPointAggregation>
  | ReturnType<typeof setBasemapStyle>
  | ReturnType<typeof setBasemapOpacity>
  | ReturnType<typeof setNavigationControl>
  | ReturnType<typeof setLayerToggleControl>
  | ReturnType<typeof setGeoCoderControl>
  | ReturnType<typeof setShowDataTableControl>
  | ReturnType<typeof setLockCalendarViewControl>
  | ReturnType<typeof setGeoLocateControl>
  | ReturnType<typeof setMapFlyoutTitleColumnName>
  | ReturnType<typeof addDrilldownColumn>
  | ReturnType<typeof removeDrilldownColumn>
  | ReturnType<typeof changeDrilldownColumn>
  | ReturnType<typeof addTableColumn>
  | ReturnType<typeof removeTableColumn>
  | ReturnType<typeof replaceAllTableColumns>
  | ReturnType<typeof resetAllColumns>
  | ReturnType<typeof reorderTableColumns>
  | ReturnType<typeof newTableHierarchy>
  | ReturnType<typeof updateTableVisualizationColumnFormat>
  | ReturnType<typeof updateTableVisualizationColumnStyle>
  | ReturnType<typeof updateTableVisualizationColumnStyleCondition>
  | ReturnType<typeof updateTableVisualizationHeaderStyle>
  | ReturnType<typeof updateTableVisualizationHeaderTextStyle>
  | ReturnType<typeof setActiveHierarchyId>
  | ReturnType<typeof showGrandTotal>
  | ReturnType<typeof showSubTotal>
  | ReturnType<typeof updateCustomLabels>
  | ReturnType<typeof applyAdvancedTotalingConfig>
  | ReturnType<typeof updateTableHierarchyName>
  | ReturnType<typeof duplicateTableHierarchy>
  | ReturnType<typeof removeTableHierarchy>
  | ReturnType<typeof addBasemapFlyoutColumn>
  | ReturnType<typeof removeBasemapFlyoutColumn>
  | ReturnType<typeof changeAdditionalFlyoutColumn>
  | ReturnType<typeof setShowAnnotationsInLegend>
  | ReturnType<typeof setAnnotations>
  | ReturnType<typeof addAnnotation>
  | ReturnType<typeof removeAnnotation>
  | ReturnType<typeof updateAnnotation>
  | ReturnType<typeof addRegionMapFlyoutColumnAndAggregation>
  | ReturnType<typeof removeRegionMapFlyoutColumnAndAggregation>
  | ReturnType<typeof changeRegionMapFlyoutColumnAndAggregation>
  | ReturnType<typeof setAdditionalFlyoutColumns>
  | ReturnType<typeof setRegionMapFlyoutColumnAndAggregations>
  | ReturnType<typeof setSearchBoundaryUpperLeftLatitude>
  | ReturnType<typeof setSearchBoundaryUpperLeftLongitude>
  | ReturnType<typeof setSearchBoundaryLowerRightLatitude>
  | ReturnType<typeof setSearchBoundaryLowerRightLongitude>
  | ReturnType<typeof setPitchAndBearing>
  | ReturnType<typeof setMapZoomLevel>
  | ReturnType<typeof setShapeFillColor>
  | ReturnType<typeof setShapeFillOpacity>
  | ReturnType<typeof setLineColorOpacity>
  | ReturnType<typeof setLineStylePoints>
  | ReturnType<typeof setLineStylePointRadius>
  | ReturnType<typeof setShapeOutlineColor>
  | ReturnType<typeof setShapeOutlineWidth>
  | ReturnType<typeof setTriggerAutoSelectGeoLocationColumn>
  | ReturnType<typeof setHideDuplicateGroups>
  | ReturnType<typeof setTableIndentedLayout>
  | ReturnType<typeof setTableRowStripeStyleAction>
  | ReturnType<typeof setTableCustomSort>
  | ReturnType<typeof setTableOrderConfig>
  | ReturnType<typeof setTableHeaderSortCycleIndex>
  | ReturnType<typeof setShowSummaryTable>;
